/**
 * QuickReminder.jsx
 * Componente UNIFICADO para gestionar recordatorios (reemplaza ReminderManager.js)
 *
 * Funcionalidades:
 * - Listar recordatorios activos (personales + proveedores)
 * - Crear recordatorios (manual o desde imagen)
 * - Editar recordatorios existentes
 * - Eliminar recordatorios con confirmación
 * - Contador de límite (máx 10)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../../services/api/config';
import { obtenerUsuarioPorId, updateUsuario, obtenerTratamientosMascota, reportarOlvido, getCalendarEvents, getCalendarStatus } from '../../services/api';
import { createPlace } from '../../localizador/services/placesService';
import { addFavorite } from '../../localizador/services/favoritesService';

// Colores del tema LemmonTags
const COLORS = {
  bgMain: '#ffffff',
  bgCard: '#ffffff',
  bgSecondary: '#f8f9fa',
  textPrimary: '#374151',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  accent: '#84cc16',
  accentHover: '#65a30d',
  accentLight: '#f0fdf4',
  border: '#e5e7eb',
  borderLight: '#d1d5db',
  danger: '#ef4444',
  dangerLight: '#fef2f2',
  warning: '#f59e0b',
};

// Frecuencias disponibles
const FRECUENCIAS = [
  { value: 'no', label: 'No se repite' },
  { value: 'diario', label: 'Diario' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'bimestral', label: 'Bimestral' },
  { value: 'anual', label: 'Anual' },
];

// Tipos de documento detectables
const TIPOS_DOCUMENTO = {
  pase_abordar: { emoji: '✈️', label: 'Vuelo', color: '#3b82f6' },
  reservacion_hotel: { emoji: '🏨', label: 'Hotel', color: '#8b5cf6' },
  boleto_transporte: { emoji: '🚌', label: 'Transporte', color: '#f59e0b' },
  boleto_evento: { emoji: '🎫', label: 'Evento', color: '#ec4899' },
  cita_medica: { emoji: '🏥', label: 'Cita Médica', color: '#ef4444' },
  cita_servicio: { emoji: '🔧', label: 'Servicio', color: '#84cc16' },
  poliza: { emoji: '📋', label: 'Póliza de seguro', color: '#0ea5e9' },
  garantia: { emoji: '🛡️', label: 'Garantía', color: '#14b8a6' },
  contrato: { emoji: '📝', label: 'Contrato', color: '#6366f1' },
  pago_servicio: { emoji: '🧾', label: 'Pago de servicio', color: '#f97316' },
};

// Íconos para tipos de servicio de pago
const ICONOS_PAGO_SERVICIO = {
  luz: '💡',
  agua: '💧',
  gas: '🔥',
  telefono: '📱',
  internet: '🌐',
  cable: '📺',
};

// Tipos de servicio para proveedores
const TIPOS_SERVICIO_PROVEEDOR = [
  { value: 'paseo', label: 'Paseo', emoji: '🐕' },
  { value: 'bano', label: 'Baño', emoji: '🛁' },
  { value: 'alimentacion', label: 'Alimentación', emoji: '🍖' },
  { value: 'cuidado', label: 'Cuidado', emoji: '🏠' },
  { value: 'veterinario', label: 'Veterinario', emoji: '🩺' },
  { value: 'otro', label: 'Otro...', emoji: '📋' },
];

// Detección de intención basada en texto del recordatorio
function detectarTipoDocumento(texto) {
  if (!texto || texto.trim().length < 3) return null;
  const t = texto.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  if (/cita|dr\.|doctor|consulta|veterinari|medic|dentist|optic|dental|odontologo|oftalmologo|oculista|ginecologo|pediatra|cardiologo|dermatologo|nutriologo|psicologo|fisioterapia|laboratorio|analisis|estudio.*medic|radiografia|ultrasonido|clinica|hospital|consultorio|especialista/.test(t)) return 'cita_medica';
  if (/pago|luz|agua|gas|internet|telefon|cable|predial|tenencia/.test(t)) return 'pago_servicio';
  if (/vuelo|avion|aeropuerto|pase.*abordar/.test(t)) return 'pase_abordar';
  if (/boleto|ado|etn|autobus|tren|camion/.test(t)) return 'boleto_transporte';
  if (/hotel|hospedaje|reservacion|airbnb/.test(t)) return 'reservacion_hotel';
  if (/evento|concierto|teatro|entrada/.test(t)) return 'boleto_evento';
  if (/poliza|seguro/.test(t)) return 'poliza';
  if (/garantia/.test(t)) return 'garantia';
  if (/contrato|renta|arrendamiento/.test(t)) return 'contrato';
  if (/servicio|reparacion|mantenimiento|instalacion/.test(t)) return 'cita_servicio';

  return null;
}

// Horas disponibles (bloques de 30 min, 8 AM - 8 PM) - legacy, mantener por compatibilidad
const HORAS_DISPONIBLES = [];
for (let h = 8; h <= 20; h++) {
  for (let m of ['00', '30']) {
    if (h === 20 && m === '30') continue;
    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const period = h >= 12 ? 'PM' : 'AM';
    HORAS_DISPONIBLES.push({
      value: `${String(h).padStart(2, '0')}:${m}`,
      label: `${hour12}:${m} ${period}`,
    });
  }
}

// Nuevos selectores de hora (más usables)
const HORAS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTOS_OPCIONES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const PERIODOS = ['AM', 'PM'];

// Convertir hora 24h (HH:MM) a componentes 12h
const hora24ToComponents = (hora24) => {
  if (!hora24) return { hour: 9, minute: '00', period: 'AM' };
  const [h, m] = hora24.split(':').map(Number);
  const minute = String(Math.round(m / 5) * 5 % 60).padStart(2, '0');
  let hour = h % 12;
  if (hour === 0) hour = 12;
  const period = h >= 12 ? 'PM' : 'AM';
  return { hour, minute, period };
};

// Convertir componentes 12h a hora 24h (HH:MM)
const componentsToHora24 = (hour, minute, period) => {
  let h = hour;
  if (period === 'PM' && hour !== 12) h = hour + 12;
  if (period === 'AM' && hour === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

// Obtener fecha mínima (mañana)
const getMinDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

// Formatear hora de 24h a 12h
const formatHora12 = (hora24) => {
  if (!hora24) return '';
  const [h, m] = hora24.split(':').map(Number);
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const period = h >= 12 ? 'PM' : 'AM';
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

/**
 * Normaliza hora de cualquier formato a HH:MM (24h)
 * Soporta: "9:00 AM", "9:00AM", "09:00", "9:00", "14:30", etc.
 */
const normalizeHora = (hora) => {
  if (!hora) return '09:00';

  const horaStr = String(hora).trim().toUpperCase();

  // Detectar AM/PM
  const isPM = horaStr.includes('PM');
  const isAM = horaStr.includes('AM');

  // Extraer números
  const cleanHora = horaStr.replace(/[APM\s]/g, '');
  const parts = cleanHora.split(':');

  let h = parseInt(parts[0], 10) || 9;
  const m = parseInt(parts[1], 10) || 0;

  // Convertir a 24h si tiene AM/PM
  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;

  // Validar rango
  if (h < 0 || h > 23) h = 9;
  if (m < 0 || m > 59) return `${String(h).padStart(2, '0')}:00`;

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Normaliza fecha de cualquier formato a YYYY-MM-DD
 * Soporta: "09/02/2026", "02-09-2026", "2026-02-09", "Feb 9, 2026", etc.
 */
const normalizeFecha = (fecha) => {
  if (!fecha) return '';

  const fechaStr = String(fecha).trim();

  // Si ya está en formato YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) {
    return fechaStr;
  }

  // Intentar parsear con Date
  let parsed;

  // Formato DD/MM/YYYY o MM/DD/YYYY
  if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}$/.test(fechaStr)) {
    const parts = fechaStr.split(/[\/\-]/);
    const a = parseInt(parts[0], 10);
    const b = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Determinar si es DD/MM o MM/DD basándose en los valores
    // Si a > 12, es DD/MM/YYYY (día primero)
    // Si b > 12, es MM/DD/YYYY (mes primero)
    // Si ambos <= 12, asumimos DD/MM/YYYY (formato más común en español)
    let day, month;
    if (a > 12) {
      day = a;
      month = b;
    } else if (b > 12) {
      month = a;
      day = b;
    } else {
      // Ambiguo - asumimos DD/MM/YYYY (formato español)
      day = a;
      month = b;
    }

    parsed = new Date(year, month - 1, day);
  } else {
    // Intentar parseo genérico
    parsed = new Date(fechaStr);
  }

  // Validar que sea una fecha válida
  if (isNaN(parsed.getTime())) {
    return '';
  }

  // Convertir a YYYY-MM-DD
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Formatear fecha para display
const formatFecha = (fechaStr) => {
  if (!fechaStr) return '';

  const fecha = new Date(fechaStr.includes('T') ? fechaStr : fechaStr + 'T00:00:00');
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);

  const fechaSolo = new Date(fecha);
  fechaSolo.setHours(0, 0, 0, 0);

  if (fechaSolo.getTime() === hoy.getTime()) return 'Hoy';
  if (fechaSolo.getTime() === manana.getTime()) return 'Mañana';

  return fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Formatear frecuencia para display
const formatFrecuencia = (recordatorio) => {
  // Para proveedores con modalidad
  if (recordatorio.modalidad) {
    if (recordatorio.modalidad === 'unico') return 'Una vez';
    if (recordatorio.modalidad === 'diario') return 'Todos los días';
    if (recordatorio.modalidad === 'diasEspecificos' && recordatorio.diasSemana) {
      if (recordatorio.diasSemana.length === 7) return 'Todos los días';
      return recordatorio.diasSemana.join(', ');
    }
  }

  // Para personales con repetir
  if (recordatorio.repetir) {
    const freq = FRECUENCIAS.find(f => f.value === recordatorio.repetir);
    return freq?.label || recordatorio.repetir;
  }

  return 'Una vez';
};

// No limit on recordatorios count

// ============= CONFIGURACIÓN DE RUTINAS POR ESPECIE =============
const RUTINAS_CONFIG = {
  paseo: { emoji: '🐕', label: 'Paseo', especies: ['perro', 'otro'] },
  comida: { emoji: '🍖', label: 'Comida', especies: ['perro', 'gato', 'otro'] },
  medicamento: { emoji: '💊', label: 'Medicamento', especies: ['perro', 'gato', 'otro'] },
  juego: { emoji: '🎾', label: 'Juego', especies: ['perro', 'gato', 'otro'] },
  arenero: { emoji: '🧹', label: 'Arenero', especies: ['gato'] },
};

// Días de la semana
const DIAS_SEMANA = [
  { value: 'L', label: 'L', nombre: 'Lunes' },
  { value: 'M', label: 'M', nombre: 'Martes' },
  { value: 'X', label: 'X', nombre: 'Miércoles' },
  { value: 'J', label: 'J', nombre: 'Jueves' },
  { value: 'V', label: 'V', nombre: 'Viernes' },
  { value: 'S', label: 'S', nombre: 'Sábado' },
  { value: 'D', label: 'D', nombre: 'Domingo' },
];

// Límites por plan para rutinas
const LIMITE_RUTINAS = {
  light: 1,
  standard: 3,
  premium: 5,
};

/**
 * Componente TimePicker - Tres selectores para hora (más usable que dropdown largo)
 * Props:
 *   value: string en formato "HH:MM" (24h)
 *   onChange: function(newValue) donde newValue es "HH:MM" (24h)
 */
function TimePicker({ value, onChange }) {
  const { hour, minute, period } = hora24ToComponents(value);

  const handleHourChange = (newHour) => {
    onChange(componentsToHora24(Number(newHour), minute, period));
  };

  const handleMinuteChange = (newMinute) => {
    onChange(componentsToHora24(hour, newMinute, period));
  };

  const handlePeriodChange = (newPeriod) => {
    onChange(componentsToHora24(hour, minute, newPeriod));
  };

  const selectStyle = {
    borderColor: COLORS.border,
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '14px',
    minWidth: '60px',
  };

  return (
    <div className="d-flex align-items-center gap-1">
      <select
        className="form-select"
        value={hour}
        onChange={(e) => handleHourChange(e.target.value)}
        style={selectStyle}
      >
        {HORAS_12.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: '16px' }}>:</span>
      <select
        className="form-select"
        value={minute}
        onChange={(e) => handleMinuteChange(e.target.value)}
        style={selectStyle}
      >
        {MINUTOS_OPCIONES.map(m => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <select
        className="form-select"
        value={period}
        onChange={(e) => handlePeriodChange(e.target.value)}
        style={{ ...selectStyle, minWidth: '70px' }}
      >
        {PERIODOS.map(p => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
    </div>
  );
}

export default function QuickReminder({
  objetoId,
  objetoNombre,
  objetoTipo = 'mascota',
  objetoEspecie = '',
  objetoPlan = 'light',
  userId,
  onClose,
  onSuccess
}) {
  // Determinar si es mascota
  const esMascota = objetoTipo === 'mascota';

  // Normalizar especie para filtrar rutinas
  const especieNormalizada = (objetoEspecie || '').toLowerCase().includes('gato') ? 'gato'
    : (objetoEspecie || '').toLowerCase().includes('perro') ? 'perro'
    : 'otro';

  // Estado principal - vistas: 'list', 'create', 'edit', 'rutina'
  const [view, setView] = useState('list');

  // Estado de recordatorios
  const [recordatorios, setRecordatorios] = useState([]);
  const [rutinas, setRutinas] = useState([]);
  const [proximas, setProximas] = useState([]);
  const [tratamientosActivos, setTratamientosActivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalActivos, setTotalActivos] = useState(0);

  // Secciones colapsables (para mascotas)
  const [seccionesAbiertas, setSeccionesAbiertas] = useState({
    rutinas: true,
    tratamientos: true,
    proximas: true,
    proveedores: true,
  });

  // Estado para creación/edición de eventos (objetos)
  const [mode, setMode] = useState(null); // null, 'manual', 'image'
  const [editingReminder, setEditingReminder] = useState(null);

  // Estado para creación de rutinas (mascotas)
  const [selectedRutina, setSelectedRutina] = useState(null);
  const [rutinaForm, setRutinaForm] = useState({
    hora: '08:00',
    diasSemana: ['L', 'M', 'X', 'J', 'V'],
  });

  const [formData, setFormData] = useState({
    que: '',
    fecha: '',
    hora: '09:00',
    repetir: 'no',
    proveedorId: '',
    tipoServicio: '',       // Nuevo: tipo de servicio para proveedores
    descripcionOtro: '',    // Nuevo: descripción cuando es "otro"
    ubicacion: null,        // Ubicación detectada/enriquecida
    // Campos para extracción inteligente de documentos
    tipoDocumento: 'cita_servicio',  // pase_abordar, reservacion_hotel, boleto_transporte, boleto_evento, cita_medica, cita_servicio, poliza, garantia, contrato, pago_servicio
    detalles: null,                  // Campos específicos según tipoDocumento
    anticipacionSugerida: 120,       // Minutos de anticipación
    notas: '',                       // Notas adicionales del documento
    acciones_sugeridas: null,        // { crear_recordatorio, guardar_favorito, guardar_boveda }
    warnings: [],                    // Advertencias de la IA
    asunciones: [],                  // Asunciones que hizo la IA
    guardarFavorito: false,          // Checkbox: guardar lugar en favoritos
    favoritoNombre: '',              // Nombre del lugar/doctor para favoritos
    favoritoTelefono: '',            // Teléfono opcional para favoritos
  });

  // Estado para proveedores
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(false);

  // Estado para detección de móvil (para UI responsive, incluyendo modal fullscreen)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Grupos colapsados en vista kanban (objetos)
  const [collapsedGroups, setCollapsedGroups] = useState({
    proxSemana: true,
    proxMes: true,
    masAdelante: true,
    vencidos: true,
  });

  // Google Calendar events (solo objetos)
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleEventsLoading, setGoogleEventsLoading] = useState(false);
  const [showGoogleEvents, setShowGoogleEvents] = useState(() => {
    try { return localStorage.getItem('qr_showGoogleEvents') !== 'false'; } catch { return true; }
  });
  const [calendarConnected, setCalendarConnected] = useState(false);
  const googleEventsCacheRef = useRef(null); // { events, fetchedAt }

  // Estados de operación
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Estados para modo imagen
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extractionError, setExtractionError] = useState(null);

  // Estado para detección de intención por texto
  const [tipoDetectado, setTipoDetectado] = useState(null);

  // Estado para edición de ubicación (Google Places Autocomplete)
  const [editandoUbicacion, setEditandoUbicacion] = useState(false);
  const [ubicacionStatus, setUbicacionStatus] = useState(null); // 'loading' | 'ready' | 'error' | 'no-api'
  const ubicacionAcRef = useRef(null);

  // Estado para dirección del usuario (pre-llenar ubicación)
  const [usuarioDireccion, setUsuarioDireccion] = useState(null);
  const usuarioDireccionCargada = useRef(false);

  // Cargar recordatorios y rutinas
  const loadRecordatorios = useCallback(async () => {
    if (!objetoId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('nfcToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (esMascota) {
        // Para MASCOTAS: cargar rutinas, proveedores, personales (próximas) y tratamientos
        // Usar Promise.allSettled para que un fallo individual no bloquee todo
        const [rutinasResult, proveedorResult, personalResult, tratamientosResult] = await Promise.allSettled([
          fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios`, { headers }),
          fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-proveedores`, { headers }),
          fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-personales`, { headers }),
          obtenerTratamientosMascota(objetoId, 'activo'),
        ]);

        let rutinasData = [];
        let proveedoresRec = [];
        let personalesData = [];

        if (rutinasResult.status === 'fulfilled' && rutinasResult.value.ok) {
          const data = await rutinasResult.value.json();
          rutinasData = (data.data?.recordatorios || []).map(r => ({ ...r, tipo: 'rutina' }));
        }

        if (proveedorResult.status === 'fulfilled' && proveedorResult.value.ok) {
          const data = await proveedorResult.value.json();
          proveedoresRec = (data.data || []).map(r => ({ ...r, tipo: 'proveedor' }));
        }

        if (personalResult.status === 'fulfilled' && personalResult.value.ok) {
          const data = await personalResult.value.json();
          personalesData = (data.data || []).filter(r => r.activo !== false);
        }

        // Tratamientos activos
        let tratamientos = [];
        if (tratamientosResult.status === 'fulfilled') {
          const tratamientosRes = tratamientosResult.value;
          if (tratamientosRes.error) {
            console.warn('[QuickReminder] Error cargando tratamientos:', tratamientosRes.error);
          } else {
            tratamientos = tratamientosRes.tratamientos || [];
          }
          console.log('[QuickReminder] tratamientosRes:', JSON.stringify({ error: tratamientosRes.error, count: tratamientosRes.tratamientos?.length, total: tratamientosRes.total }));
        } else {
          console.warn('[QuickReminder] Promise rechazada para tratamientos:', tratamientosResult.reason);
        }

        // Filtrar activos
        const rutinasActivas = rutinasData.filter(r => r.activo !== false);
        const proveedoresActivos = proveedoresRec.filter(r => r.activo !== false);

        setRutinas(rutinasActivas);
        setRecordatorios(proveedoresActivos);
        setProximas(personalesData);
        setTratamientosActivos(tratamientos);
        setTotalActivos(rutinasActivas.length + proveedoresActivos.length + personalesData.length);

      } else {
        // Para OBJETOS: cargar eventos personales, proveedores y recordatorios de objetos
        const [personalRes, proveedorRes, objetosRes] = await Promise.all([
          fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-personales`, { headers }),
          fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-proveedores`, { headers }),
          fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-objetos`, { headers })
        ]);

        let personales = [];
        let proveedoresRec = [];
        let objetosRec = [];

        if (personalRes.ok) {
          const data = await personalRes.json();
          personales = (data.data || []).map(r => ({ ...r, tipo: 'personal' }));
        }

        if (proveedorRes.ok) {
          const data = await proveedorRes.json();
          proveedoresRec = (data.data || []).map(r => ({ ...r, tipo: 'proveedor' }));
        }

        if (objetosRes.ok) {
          const data = await objetosRes.json();
          objetosRec = (data.data || []).map(r => ({
            ...r,
            id: r.recordatorioId,
            tipo: 'objeto'
          }));
        }

        // Combinar y ordenar por próximo envío
        const todos = [...personales, ...proveedoresRec, ...objetosRec]
          .filter(r => r.activo !== false)
          .sort((a, b) => {
            const fechaA = a.proximoEnvio2Horas || a.proximoEnvio || a.fechaEvento || a.fechaServicio || a.fechaUnica || '';
            const fechaB = b.proximoEnvio2Horas || b.proximoEnvio || b.fechaEvento || b.fechaServicio || b.fechaUnica || '';
            return new Date(fechaA) - new Date(fechaB);
          });

        setRecordatorios(todos);
        setTotalActivos(todos.length);
      }

    } catch (error) {
      console.error('Error cargando recordatorios:', error);
      toast.error('Error al cargar recordatorios');
    } finally {
      setLoading(false);
    }
  }, [objetoId, esMascota]);

  // Cargar proveedores
  const loadProveedores = useCallback(async () => {
    if (!userId) return;

    setLoadingProveedores(true);
    try {
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`${API_URL}/api/proveedores/porOwner/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Filtrar solo proveedores verificados (estado === 'verificado')
        const verificados = (result.data || []).filter(p => p.estado === 'verificado');
        setProveedores(verificados);
      }
    } catch (error) {
      console.error('Error cargando proveedores:', error);
    } finally {
      setLoadingProveedores(false);
    }
  }, [userId]);

  // Cargar datos iniciales
  useEffect(() => {
    loadRecordatorios();
    loadProveedores();
  }, [loadRecordatorios, loadProveedores]);

  // Cargar eventos de Google Calendar (solo para objetos)
  const loadGoogleCalendarEvents = useCallback(async (forceRefresh = false) => {
    if (esMascota || !userId) return;

    // Use cache if available and not forcing refresh
    if (!forceRefresh && googleEventsCacheRef.current) {
      setGoogleEvents(googleEventsCacheRef.current.events);
      setCalendarConnected(true);
      return;
    }

    setGoogleEventsLoading(true);
    try {
      // Check connection status first
      const status = await getCalendarStatus();
      if (!status?.connected) {
        setCalendarConnected(false);
        setGoogleEvents([]);
        setGoogleEventsLoading(false);
        return;
      }
      setCalendarConnected(true);

      // Calculate date range: start of this week to +2 months
      const now = new Date();
      const startOfWeek = new Date(now);
      const day = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - (day === 0 ? 6 : day - 1));
      const from = startOfWeek.toISOString().split('T')[0];

      const twoMonthsLater = new Date(now);
      twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2);
      const to = twoMonthsLater.toISOString().split('T')[0];

      const result = await getCalendarEvents(from, to);
      if (result?.events) {
        setGoogleEvents(result.events);
        googleEventsCacheRef.current = { events: result.events, fetchedAt: Date.now() };
      }
    } catch (err) {
      // Graceful degradation — don't show errors
      console.warn('[QuickReminder] Error loading Google Calendar events:', err);
    } finally {
      setGoogleEventsLoading(false);
    }
  }, [esMascota, userId]);

  useEffect(() => {
    loadGoogleCalendarEvents();
  }, [loadGoogleCalendarEvents]);

  // Persist toggle state in localStorage
  const toggleGoogleEvents = useCallback(() => {
    setShowGoogleEvents(prev => {
      const next = !prev;
      try { localStorage.setItem('qr_showGoogleEvents', String(next)); } catch {}
      return next;
    });
  }, []);

  // Cargar dirección del usuario para pre-llenar ubicación
  useEffect(() => {
    if (!userId || usuarioDireccionCargada.current) return;

    const cargarDireccionUsuario = async () => {
      try {
        const usuario = await obtenerUsuarioPorId(userId);
        if (usuario && !usuario.error && usuario.direccion) {
          console.log('[QuickReminder] Dirección del usuario cargada:', usuario.direccion);
          setUsuarioDireccion(usuario.direccion);
          // Pre-llenar ubicación - usar setFormData con función para evitar dependencia
          setFormData(prev => {
            if (prev.ubicacion) return prev; // No sobrescribir si ya hay ubicación
            // Convertir dirección string a objeto ubicación compatible
            const ubicacionPrefill = typeof usuario.direccion === 'string'
              ? { nombre: usuario.direccion, direccion: usuario.direccion }
              : usuario.direccion;
            return { ...prev, ubicacion: ubicacionPrefill };
          });
        }
        usuarioDireccionCargada.current = true;
      } catch (error) {
        console.warn('[QuickReminder] Error cargando dirección del usuario:', error);
        usuarioDireccionCargada.current = true;
      }
    };

    cargarDireccionUsuario();
  }, [userId]);

  // Detectar cambios de tamaño de pantalla para UI responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Callback ref: se ejecuta cuando el input aparece/desaparece del DOM.
  // Elimina la race condition del useEffect — solo corre cuando el nodo realmente existe.
  const ubicacionRefCallback = useCallback((node) => {
    if (!node) {
      // El input se desmontó
      ubicacionAcRef.current = null;
      return;
    }

    // Evitar doble instanciación si ya hay un autocomplete en este nodo
    if (ubicacionAcRef.current) return;

    setUbicacionStatus('loading');

    const initAutocomplete = async () => {
      try {
        // Esperar a que google.maps esté disponible (max 10s)
        if (!window.google?.maps) {
          await new Promise((resolve, reject) => {
            let attempts = 0;
            const check = setInterval(() => {
              attempts++;
              if (window.google?.maps) { clearInterval(check); resolve(); }
              else if (attempts > 20) { clearInterval(check); reject(new Error('Google Maps API no cargó')); }
            }, 500);
          });
        }

        const { Autocomplete } = await window.google.maps.importLibrary('places');

        const ac = new Autocomplete(node, {
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: 'mx' },
          fields: ['formatted_address', 'name', 'geometry', 'place_id'],
        });

        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (place?.geometry) {
            const nombre = place.name || place.formatted_address;
            const nuevaUbicacion = {
              nombre,
              direccion: place.formatted_address || '',
              coordenadas: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              },
              placeId: place.place_id || null,
              googleMapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name || '')}&query_place_id=${place.place_id || ''}`,
            };
            setFormData(prev => ({
              ...prev,
              ubicacion: nuevaUbicacion,
              favoritoNombre: prev.favoritoNombre || nombre,
            }));
            setEditandoUbicacion(false);
            toast.success('Ubicación actualizada');
          }
        });

        ubicacionAcRef.current = ac;
        setUbicacionStatus('ready');
      } catch (err) {
        console.error('[QuickReminder] Error Places API:', err);
        setUbicacionStatus(err.message?.includes('no cargó') ? 'no-api' : 'error');
      }
    };

    initAutocomplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler para cambio de campo
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Detectar intención al perder foco del campo "¿Qué?"
  const handleQueBlur = () => {
    if (editingReminder) return; // No detectar al editar
    const tipo = detectarTipoDocumento(formData.que);
    setTipoDetectado(tipo);
    if (tipo && tipo !== formData.tipoDocumento) {
      handleChange('tipoDocumento', tipo);
      // Pre-seleccionar guardarFavorito para citas con ubicación
      if (['cita_medica', 'cita_servicio'].includes(tipo) && formData.ubicacion) {
        handleChange('guardarFavorito', false); // Mostrar checkbox pero no preseleccionar
      }
    }
  };

  // Reset del formulario
  const resetForm = () => {
    setFormData({
      que: '',
      fecha: '',
      hora: '09:00',
      repetir: 'no',
      proveedorId: '',
      tipoServicio: '',
      descripcionOtro: '',
      ubicacion: null,
      tipoDocumento: 'cita_servicio',
      detalles: null,
      anticipacionSugerida: 120,
      notas: '',
      acciones_sugeridas: null,
      warnings: [],
      asunciones: [],
      guardarFavorito: false,
      favoritoNombre: '',
      favoritoTelefono: '',
    });
    setMode(null);
    setEditingReminder(null);
    setSelectedImage(null);
    setImagePreview(null);
    setExtractedData(null);
    setExtractionError(null);
    setEditandoUbicacion(false);
    setTipoDetectado(null);
  };

  // Iniciar creación
  const startCreate = () => {
    resetForm();
    setView('create');
  };

  // Iniciar edición
  const startEdit = (reminder) => {
    setEditingReminder(reminder);

    // Pre-llenar formulario según tipo
    if (reminder.tipo === 'personal') {
      setFormData({
        que: reminder.que || '',
        fecha: reminder.fecha || '',
        hora: reminder.hora || '09:00',
        repetir: reminder.repetir || 'no',
        proveedorId: '',
      });
    } else if (reminder.tipo === 'objeto') {
      // Recordatorio de objeto/documento - mapear a campos del form
      setFormData({
        que: reminder.nombrePersonalizado || '',
        fecha: reminder.fechaUnica || '',
        hora: '09:00',
        repetir: 'no',
        proveedorId: '',
      });
    } else {
      // Proveedor
      const fechaServicio = reminder.fechaServicio?.split('T')[0] || '';
      setFormData({
        que: reminder.instrucciones || reminder.tipoServicio || '',
        fecha: fechaServicio,
        hora: reminder.horaServicio || reminder.hora || '09:00',
        repetir: reminder.modalidad === 'unico' ? 'no' : (reminder.modalidad === 'diario' ? 'diario' : 'semanal'),
        proveedorId: reminder.proveedorId || '',
      });
    }

    setMode('manual');
    setView('edit');
  };

  // Handler para seleccionar imagen
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máximo 10MB)');
      return;
    }

    setSelectedImage(file);
    setExtractionError(null);
    setExtractedData(null);

    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result);
    reader.readAsDataURL(file);
  };

  // Handler para extraer datos de imagen
  const handleExtractFromImage = async () => {
    if (!selectedImage) return;

    setExtracting(true);
    setExtractionError(null);

    try {
      const token = localStorage.getItem('nfcToken');

      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result);
        reader.readAsDataURL(selectedImage);
      });

      const response = await fetch(`${API_URL}/api/ai/extract-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image: base64,
          context: `Extrayendo recordatorio para ${objetoTipo}: ${objetoNombre}`
        })
      });

      if (!response.ok) throw new Error('Error al procesar la imagen');

      const result = await response.json();

      if (result.error) {
        setExtractionError(result.error);
        toast.warning(result.error);
      } else {
        setExtractedData(result);
        // Normalizar fecha y hora a los formatos esperados por el backend
        const fechaNormalizada = normalizeFecha(result.fecha);
        const horaNormalizada = normalizeHora(result.hora);

        // Obtener ubicación enriquecida si existe
        const ubicacionEnriquecida = result.ubicacionEnriquecida || null;

        setFormData(prev => ({
          ...prev,
          que: result.que || prev.que,
          fecha: fechaNormalizada || prev.fecha,
          hora: horaNormalizada || prev.hora,
          repetir: result.repetir || prev.repetir,
          ubicacion: ubicacionEnriquecida,
          // Nuevos campos de extracción inteligente
          tipoDocumento: result.tipoDocumento || 'cita_servicio',
          detalles: result.detalles || null,
          anticipacionSugerida: result.anticipacionSugerida || 120,
          notas: result.notas || prev.notas,
          acciones_sugeridas: result.acciones_sugeridas || null,
          warnings: result.warnings || [],
          asunciones: result.asunciones || [],
          guardarFavorito: result.acciones_sugeridas?.guardar_favorito || false,
          favoritoNombre: ubicacionEnriquecida?.nombre || '',
          favoritoTelefono: ubicacionEnriquecida?.telefono || '',
        }));

        // Log detallado para debugging
        console.log('[QuickReminder] ========== Datos extraídos ==========');
        console.log('[QuickReminder] Tipo documento:', result.tipoDocumento || 'cita_servicio');
        console.log('[QuickReminder] Datos originales:', {
          que: result.que,
          fecha: result.fecha,
          hora: result.hora,
          ubicacion: result.ubicacion,
        });
        console.log('[QuickReminder] Detalles específicos:', result.detalles);
        console.log('[QuickReminder] Anticipación sugerida:', result.anticipacionSugerida, 'minutos');
        console.log('[QuickReminder] Ubicación enriquecida:', ubicacionEnriquecida);
        if (ubicacionEnriquecida) {
          console.log('[QuickReminder] → nombre:', ubicacionEnriquecida.nombre);
          console.log('[QuickReminder] → direccion:', ubicacionEnriquecida.direccion || '(no disponible)');
          console.log('[QuickReminder] → telefono:', ubicacionEnriquecida.telefono || '(no disponible)');
          console.log('[QuickReminder] → googleMapsUrl:', ubicacionEnriquecida.googleMapsUrl || '(no disponible)');
        }
        console.log('[QuickReminder] ====================================');

        toast.success('Datos extraídos correctamente');
        setMode('manual');
      }
    } catch (error) {
      console.error('Error extrayendo datos:', error);
      setExtractionError('No se pudo procesar la imagen. Intenta de nuevo.');
      toast.error('Error al procesar la imagen');
    } finally {
      setExtracting(false);
    }
  };

  // Mapear frecuencia a modalidad del sistema de proveedores
  const mapFrecuenciaToModalidad = (frecuencia) => {
    switch (frecuencia) {
      case 'diario': return 'diario';
      case 'semanal':
      case 'mensual':
      case 'bimestral':
      case 'anual':
        return 'diasEspecificos';
      default: return 'unico';
    }
  };

  // Guardar recordatorio (crear o editar)
  const handleSave = async () => {
    // Validaciones
    if (!formData.que.trim()) {
      toast.error('Ingresa qué quieres recordar');
      return;
    }

    if (formData.que.length > 100) {
      toast.error('El texto es demasiado largo (máximo 100 caracteres)');
      return;
    }

    if (!formData.fecha) {
      toast.error('Selecciona una fecha');
      return;
    }

    const fechaSeleccionada = new Date(formData.fecha + 'T00:00:00');
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    if (fechaSeleccionada <= hoy) {
      toast.error('La fecha debe ser mañana o después');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('nfcToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let response;

      if (editingReminder) {
        // EDICIÓN
        if (editingReminder.tipo === 'personal') {
          response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio-personal/${editingReminder.id}`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify({
              que: formData.que.trim(),
              fecha: formData.fecha,
              hora: formData.hora,
              repetir: formData.repetir,
            })
          });
        } else if (editingReminder.tipo === 'objeto') {
          // Recordatorio de objeto/documento
          response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-objetos/${editingReminder.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              nombrePersonalizado: formData.que.trim(),
              fechaUnica: formData.fecha,
            })
          });
        } else {
          // Proveedor
          response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio-proveedor/${editingReminder.id}`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify({
              instrucciones: formData.que.trim(),
              horaServicio: formData.hora,
              modalidad: mapFrecuenciaToModalidad(formData.repetir),
              fechaServicio: formData.repetir === 'no' ? `${formData.fecha}T${formData.hora}:00` : undefined,
              diasSemana: formData.repetir !== 'no' ? ['L', 'M', 'X', 'J', 'V', 'S', 'D'] : undefined,
            })
          });
        }

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || error.error || 'Error al actualizar');
        }

        toast.success(editingReminder.tipo === 'proveedor'
          ? 'Recordatorio actualizado y proveedor notificado'
          : 'Recordatorio actualizado');

      } else {
        // CREACIÓN
        if (formData.proveedorId) {
          const proveedor = proveedores.find(p => p.id === formData.proveedorId);

          response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio-proveedor`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              proveedorId: formData.proveedorId,
              proveedorNombre: proveedor?.nombre || 'Proveedor',
              proveedorEmail: proveedor?.correo || '',
              tipoServicio: 'otro',
              descripcion: formData.que,
              horaServicio: formData.hora,
              modalidad: mapFrecuenciaToModalidad(formData.repetir),
              fechaServicio: formData.repetir === 'no' ? `${formData.fecha}T${formData.hora}:00` : undefined,
              diasSemana: formData.repetir !== 'no' ? ['L', 'M', 'X', 'J', 'V', 'S', 'D'] : undefined,
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || error.error || 'Error al crear');
          }

          toast.success('Recordatorio creado y proveedor notificado');
        } else {
          response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio-personal`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              que: formData.que.trim(),
              fecha: formData.fecha,
              hora: formData.hora,
              repetir: formData.repetir,
              objetoId,
              objetoNombre,
              objetoTipo,
              // Incluir ubicación si existe (datos enriquecidos de Google Places)
              ubicacion: formData.ubicacion || undefined,
              // Nuevos campos de extracción inteligente
              tipoDocumento: formData.tipoDocumento || 'cita_servicio',
              detalles: formData.detalles || undefined,
              anticipacionSugerida: formData.anticipacionSugerida || 120,
              notas: formData.notas || undefined,
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || error.error || 'Error al crear');
          }

          toast.success('Recordatorio creado');

          // Guardar dirección en perfil del usuario si es nueva
          if (formData.ubicacion && !usuarioDireccion && userId) {
            try {
              const direccionParaGuardar = formData.ubicacion.direccion || formData.ubicacion.nombre || formData.ubicacion;
              await updateUsuario(userId, { direccion: direccionParaGuardar });
              console.log('[QuickReminder] Dirección guardada en perfil del usuario');
            } catch (err) {
              console.warn('[QuickReminder] No se pudo guardar la dirección en el perfil:', err);
            }
          }

          // Guardar lugar en favoritos si el usuario lo solicitó
          if (formData.guardarFavorito && formData.favoritoNombre.trim()) {
            try {
              const categoria = formData.tipoDocumento === 'cita_medica' ? 'Veterinaria' : 'Conveniencia';
              const lugar = await createPlace({
                tipo: 'establecimiento',
                categoria,
                nombre: formData.favoritoNombre.trim(),
                direccion: formData.ubicacion?.direccion || '',
                telefono: formData.favoritoTelefono.trim() || null,
              });
              await addFavorite(lugar.id, 'general', 'general');
              toast.success('Lugar guardado en favoritos');
            } catch (err) {
              console.warn('[QuickReminder] No se pudo guardar en favoritos:', err);
            }
          }
        }
      }

      // Recargar lista y volver
      await loadRecordatorios();
      setView('list');
      resetForm();
      onSuccess?.();

    } catch (error) {
      console.error('Error guardando:', error);
      toast.error(error.message || 'Error al guardar el recordatorio');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar recordatorio
  const handleDelete = async (reminder) => {
    setDeleting(reminder.id);

    try {
      const token = localStorage.getItem('nfcToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let endpoint;
      if (reminder.tipo === 'objeto') {
        endpoint = `${API_URL}/api/objetos/${objetoId}/recordatorios-objetos/${reminder.id}`;
      } else if (reminder.tipo === 'personal') {
        endpoint = `${API_URL}/api/objetos/${objetoId}/recordatorio-personal/${reminder.id}`;
      } else {
        endpoint = `${API_URL}/api/objetos/${objetoId}/recordatorio-proveedor/${reminder.id}`;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Error al eliminar');
      }

      toast.success(reminder.tipo === 'proveedor'
        ? 'Recordatorio eliminado y proveedor notificado'
        : 'Recordatorio eliminado');

      await loadRecordatorios();
      setConfirmDelete(null);

    } catch (error) {
      console.error('Error eliminando:', error);
      toast.error(error.message || 'Error al eliminar el recordatorio');
    } finally {
      setDeleting(null);
    }
  };

  // Obtener descripción del recordatorio
  const getReminderTitle = (reminder) => {
    if (reminder.tipo === 'personal') {
      return reminder.que || 'Recordatorio';
    }
    if (reminder.tipo === 'objeto') {
      return reminder.nombrePersonalizado || 'Recordatorio';
    }
    return reminder.instrucciones || reminder.tipoServicio || 'Servicio';
  };

  // Obtener fecha/hora del recordatorio
  const getReminderDateTime = (reminder) => {
    if (reminder.tipo === 'personal') {
      return {
        fecha: reminder.fecha,
        hora: reminder.hora
      };
    }
    if (reminder.tipo === 'objeto') {
      return {
        fecha: reminder.fechaUnica || '',
        hora: null
      };
    }
    // Proveedor
    const fechaServicio = reminder.fechaServicio?.split('T')[0] || '';
    return {
      fecha: fechaServicio,
      hora: reminder.horaServicio || reminder.hora
    };
  };

  // Calcular próximo envío
  const getProximoEnvio = (reminder) => {
    const proximo = reminder.proximoEnvio2Horas || reminder.proximoEnvio || reminder.fechaEvento || reminder.fechaServicio;
    if (!proximo) return null;

    const fecha = new Date(proximo);
    const ahora = new Date();
    const diffMs = fecha - ahora;
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHoras < 0) return 'Pasado';
    if (diffHoras < 1) return 'En menos de 1 hora';
    if (diffHoras < 24) return `En ${diffHoras} hora${diffHoras > 1 ? 's' : ''}`;
    if (diffDias === 1) return 'Mañana';
    if (diffDias < 7) return `En ${diffDias} días`;

    return formatFecha(proximo);
  };

  // ============= RENDER: Vista kanban por horizonte temporal (objetos) =============
  const renderObjectKanban = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: COLORS.accent }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2" style={{ color: COLORS.textSecondary }}>
            Cargando recordatorios...
          </p>
        </div>
      );
    }

    const hasGoogleEventsToShow = showGoogleEvents && calendarConnected && googleEvents.length > 0;
    if (recordatorios.length === 0 && !hasGoogleEventsToShow) {
      return (
        <div className="text-center py-5">
          <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.5 }}>🔔</div>
          <p style={{ color: COLORS.textSecondary, marginBottom: '24px' }}>
            No tienes recordatorios activos
          </p>
          <button
            className="btn"
            onClick={startCreate}
            style={{
              backgroundColor: COLORS.accent,
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: 600,
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Crear primer recordatorio
          </button>
        </div>
      );
    }

    // Calcular límites de cada horizonte temporal
    const ahora = new Date();
    const hoy = new Date(ahora);
    hoy.setHours(0, 0, 0, 0);

    // Fin de esta semana (domingo 23:59:59)
    const finSemana = new Date(hoy);
    const diaSemana = hoy.getDay(); // 0=dom, 1=lun...
    const diasHastaDom = diaSemana === 0 ? 0 : 7 - diaSemana;
    finSemana.setDate(finSemana.getDate() + diasHastaDom);
    finSemana.setHours(23, 59, 59, 999);

    // Fin de próxima semana
    const finProxSemana = new Date(finSemana);
    finProxSemana.setDate(finProxSemana.getDate() + 7);

    // Fin de próximo mes (30 días desde hoy)
    const finProxMes = new Date(hoy);
    finProxMes.setDate(finProxMes.getDate() + 30);
    finProxMes.setHours(23, 59, 59, 999);

    // Clasificar cada recordatorio
    const grupos = {
      vencidos: [],
      estaSemana: [],
      proxSemana: [],
      proxMes: [],
      masAdelante: [],
    };

    const classifyByDate = (fechaStr, item) => {
      if (!fechaStr) {
        grupos.masAdelante.push(item);
        return;
      }
      const fechaDate = new Date(fechaStr.includes('T') ? fechaStr : fechaStr + 'T00:00:00');
      fechaDate.setHours(0, 0, 0, 0);

      if (fechaDate < hoy) {
        grupos.vencidos.push(item);
      } else if (fechaDate <= finSemana) {
        grupos.estaSemana.push(item);
      } else if (fechaDate <= finProxSemana) {
        grupos.proxSemana.push(item);
      } else if (fechaDate <= finProxMes) {
        grupos.proxMes.push(item);
      } else {
        grupos.masAdelante.push(item);
      }
    };

    recordatorios.forEach((reminder) => {
      const { fecha } = getReminderDateTime(reminder);
      classifyByDate(fecha, reminder);
    });

    // Mix in Google Calendar events if connected and visible
    if (showGoogleEvents && calendarConnected && googleEvents.length > 0) {
      googleEvents.forEach((ev) => {
        const gcItem = { ...ev, _isGoogleEvent: true };
        // For all-day events use start date, otherwise extract date part
        const fecha = ev.allDay ? ev.start : (ev.start ? ev.start.split('T')[0] : null);
        classifyByDate(fecha, gcItem);
      });

      // Sort each group so items are interleaved by date
      Object.keys(grupos).forEach((key) => {
        grupos[key].sort((a, b) => {
          const getDate = (item) => {
            if (item._isGoogleEvent) return item.start || '';
            const { fecha } = getReminderDateTime(item);
            return fecha || '';
          };
          return new Date(getDate(a)) - new Date(getDate(b));
        });
      });
    }

    // Texto relativo para cada recordatorio
    const getRelativeText = (reminder) => {
      const { fecha } = getReminderDateTime(reminder);
      if (!fecha) return '';

      const fechaDate = new Date(fecha.includes('T') ? fecha : fecha + 'T00:00:00');
      fechaDate.setHours(0, 0, 0, 0);

      const diffMs = fechaDate - hoy;
      const diffDias = Math.round(diffMs / (1000 * 60 * 60 * 24));

      if (diffDias < 0) {
        const absDias = Math.abs(diffDias);
        if (absDias === 1) return 'Venció ayer';
        return `Vencido hace ${absDias} días`;
      }
      if (diffDias === 0) return 'Hoy';
      if (diffDias === 1) return 'Mañana';
      return `En ${diffDias} días`;
    };

    // Configuración visual de cada grupo (Esta semana primero, Vencidos al final)
    const grupoConfig = [
      {
        key: 'estaSemana',
        label: 'Esta semana',
        emoji: '🟢',
        bgHeader: '#f0fdf4',
        borderColor: '#86efac',
        textColor: '#16a34a',
        badgeBg: '#16a34a',
      },
      {
        key: 'proxSemana',
        label: 'Próxima semana',
        emoji: '🔵',
        bgHeader: '#eff6ff',
        borderColor: '#93c5fd',
        textColor: '#2563eb',
        badgeBg: '#2563eb',
      },
      {
        key: 'proxMes',
        label: 'Próximo mes',
        emoji: '🟡',
        bgHeader: '#fffbeb',
        borderColor: '#fcd34d',
        textColor: '#d97706',
        badgeBg: '#d97706',
      },
      {
        key: 'masAdelante',
        label: 'Más adelante',
        emoji: '⚪',
        bgHeader: '#f9fafb',
        borderColor: '#d1d5db',
        textColor: '#6b7280',
        badgeBg: '#6b7280',
      },
      {
        key: 'vencidos',
        label: 'Vencidos',
        emoji: '🔴',
        bgHeader: '#fef2f2',
        borderColor: '#fca5a5',
        textColor: '#dc2626',
        badgeBg: '#dc2626',
      },
    ];

    const toggleGroup = (key) => {
      setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Render de una tarjeta de recordatorio dentro del kanban
    const renderKanbanCard = (reminder) => {
      const { fecha, hora } = getReminderDateTime(reminder);
      const relText = getRelativeText(reminder);

      return (
        <div
          key={reminder.id}
          style={{
            backgroundColor: '#fff',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '8px',
          }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span style={{ fontSize: '16px' }}>
                  {reminder.tipo === 'proveedor' ? '👤' : reminder.tipo === 'objeto' ? '📄' : '📅'}
                </span>
                <strong style={{
                  color: COLORS.textPrimary,
                  fontSize: '14px',
                  wordBreak: 'break-word',
                }}>
                  {getReminderTitle(reminder)}
                </strong>
              </div>

              <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginLeft: '24px' }}>
                <span>{formatFecha(fecha)}</span>
                {hora && (
                  <>
                    <span style={{ margin: '0 4px' }}>·</span>
                    <span>{formatHora12(hora)}</span>
                  </>
                )}
                <span style={{ margin: '0 4px' }}>·</span>
                <span>{reminder.tipo === 'objeto' ? (reminder.modalidad === 'unico' ? 'Una vez' : reminder.modalidad || 'Una vez') : formatFrecuencia(reminder)}</span>
              </div>

              {reminder.tipo === 'objeto' && reminder.monto != null && (
                <div style={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary, marginLeft: '24px', marginTop: '4px' }}>
                  ${parseFloat(reminder.monto).toLocaleString('es-MX')} {reminder.moneda || 'MXN'}
                </div>
              )}

              <div style={{ fontSize: '11px', marginTop: '4px', marginLeft: '24px' }}>
                {reminder.tipo === 'proveedor' ? (
                  <span style={{
                    backgroundColor: COLORS.accentLight,
                    color: COLORS.accentHover,
                    padding: '1px 6px',
                    borderRadius: '10px',
                    fontWeight: 500,
                  }}>
                    {reminder.proveedorNombre || 'Proveedor'}
                  </span>
                ) : reminder.tipo === 'objeto' ? (
                  <span style={{
                    backgroundColor: '#ecfccb',
                    color: '#4d7c0f',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    fontWeight: 500,
                  }}>
                    {reminder.documentoNombre || 'Documento'}
                  </span>
                ) : (
                  <span style={{
                    backgroundColor: COLORS.bgSecondary,
                    color: COLORS.textSecondary,
                    padding: '1px 6px',
                    borderRadius: '10px',
                  }}>
                    Personal
                  </span>
                )}

                {relText && (
                  <span style={{
                    marginLeft: '6px',
                    color: COLORS.textMuted,
                    fontStyle: 'italic',
                  }}>
                    {relText}
                  </span>
                )}

                {reminder.googleEventId && (
                  <span
                    title="Sincronizado con Google Calendar"
                    style={{
                      marginLeft: '6px',
                      color: '#4285f4',
                      fontSize: '12px',
                    }}
                  >
                    <i className="bi bi-calendar-check-fill"></i>
                  </span>
                )}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="d-flex gap-1" style={{ marginLeft: '8px', flexShrink: 0 }}>
              <button
                className="btn btn-sm"
                onClick={() => startEdit(reminder)}
                title="Editar"
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.textSecondary,
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '13px',
                }}
              >
                <i className="bi bi-pencil"></i>
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setConfirmDelete(reminder)}
                title="Eliminar"
                disabled={deleting === reminder.id}
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${COLORS.danger}`,
                  color: COLORS.danger,
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '13px',
                }}
              >
                {deleting === reminder.id ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  <i className="bi bi-trash"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    };

    // Render de una tarjeta de evento de Google Calendar
    const renderGoogleEventCard = (event) => {
      const formatGoogleTime = (dateStr) => {
        if (!dateStr || !dateStr.includes('T')) return '';
        const d = new Date(dateStr);
        let h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, '0');
        const period = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${m} ${period}`;
      };

      const startTime = event.allDay ? '' : formatGoogleTime(event.start);
      const endTime = event.allDay ? '' : formatGoogleTime(event.end);
      const timeRange = startTime && endTime ? `${startTime}-${endTime}` : startTime;

      const startDate = event.allDay ? event.start : (event.start ? event.start.split('T')[0] : '');

      return (
        <div
          key={`gc-${event.id}`}
          onClick={() => { if (event.htmlLink) window.open(event.htmlLink, '_blank', 'noopener'); }}
          style={{
            backgroundColor: '#F5F5F5',
            border: '1px solid #E3F2FD',
            borderLeft: '3px solid #4285F4',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '8px',
            cursor: event.htmlLink ? 'pointer' : 'default',
          }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span style={{ fontSize: '16px' }}>📆</span>
                <strong style={{
                  color: COLORS.textPrimary,
                  fontSize: '14px',
                  wordBreak: 'break-word',
                }}>
                  {event.title}
                </strong>
              </div>

              <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginLeft: '24px' }}>
                <span>{formatFecha(startDate)}</span>
                {event.allDay ? (
                  <span style={{ margin: '0 4px', fontStyle: 'italic' }}>(todo el día)</span>
                ) : timeRange ? (
                  <>
                    <span style={{ margin: '0 4px' }}>·</span>
                    <span>{timeRange}</span>
                  </>
                ) : null}
                {event.location && (
                  <>
                    <span style={{ margin: '0 4px' }}>·</span>
                    <span>{event.location}</span>
                  </>
                )}
              </div>

              <div style={{ fontSize: '11px', marginTop: '4px', marginLeft: '24px' }}>
                <span style={{
                  backgroundColor: '#E8EAF6',
                  color: '#5C6BC0',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  fontWeight: 500,
                }}>
                  Google
                </span>
              </div>
            </div>

            {event.htmlLink && (
              <div style={{ marginLeft: '8px', flexShrink: 0, color: '#4285F4', fontSize: '14px' }}>
                <i className="bi bi-box-arrow-up-right"></i>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="py-2">
        <div style={{ maxHeight: isMobile ? 'none' : '400px', overflowY: isMobile ? 'visible' : 'auto' }}>
          {grupoConfig.map(({ key, label, emoji, bgHeader, borderColor, textColor, badgeBg }) => {
            const items = grupos[key];
            if (items.length === 0) return null;

            const isCollapsed = collapsedGroups[key] === true;

            return (
              <div
                key={key}
                style={{
                  marginBottom: '12px',
                  borderRadius: '10px',
                  border: `1px solid ${borderColor}`,
                  overflow: 'hidden',
                }}
              >
                {/* Header del grupo */}
                <div
                  onClick={() => toggleGroup(key)}
                  style={{
                    backgroundColor: bgHeader,
                    padding: '10px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    userSelect: 'none',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: '14px' }}>{emoji}</span>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: textColor }}>
                      {label}
                    </span>
                    <span style={{
                      backgroundColor: badgeBg,
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '1px 7px',
                      borderRadius: '10px',
                      minWidth: '20px',
                      textAlign: 'center',
                    }}>
                      {items.length}
                    </span>
                  </div>
                  <i
                    className={`bi bi-chevron-${isCollapsed ? 'down' : 'up'}`}
                    style={{ color: textColor, fontSize: '14px' }}
                  ></i>
                </div>

                {/* Cards del grupo */}
                {!isCollapsed && (
                  <div style={{ padding: '8px 10px 4px' }}>
                    {items.map((item) =>
                      item._isGoogleEvent
                        ? renderGoogleEventCard(item)
                        : renderKanbanCard(item)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Spinner de carga Google Calendar */}
        {googleEventsLoading && (
          <div className="text-center py-2">
            <span className="spinner-border spinner-border-sm" style={{ color: '#4285F4' }} role="status"></span>
            <small className="ms-2" style={{ color: COLORS.textMuted }}>Cargando eventos de Google...</small>
          </div>
        )}

        {/* Botón agregar */}
        <button
          className="btn w-100 mt-3"
          onClick={startCreate}
          style={{
            backgroundColor: COLORS.accent,
            color: '#fff',
            border: 'none',
            padding: '14px',
            borderRadius: '10px',
            fontWeight: 600,
            fontSize: '15px',
          }}
        >
          <i className="bi bi-plus-lg me-2"></i>
          Agregar Recordatorio
        </button>

        {/* CSS animation for refresh spinner */}
        <style>{`
          @keyframes spin-refresh { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .spin-animation { animation: spin-refresh 1s linear infinite; display: inline-block; }
        `}</style>
      </div>
    );
  };

  // ============= RENDER: Lista de recordatorios =============
  const renderList = () => (
    <div className="py-2">
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: COLORS.accent }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2" style={{ color: COLORS.textSecondary }}>
            Cargando recordatorios...
          </p>
        </div>
      ) : recordatorios.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.5 }}>🔔</div>
          <p style={{ color: COLORS.textSecondary, marginBottom: '24px' }}>
            No tienes recordatorios activos
          </p>
          <button
            className="btn"
            onClick={startCreate}
            style={{
              backgroundColor: COLORS.accent,
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: 600,
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Crear primer recordatorio
          </button>
        </div>
      ) : (
        <>
          {/* Lista de recordatorios */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {recordatorios.map((reminder) => {
              const { fecha, hora } = getReminderDateTime(reminder);
              const proximo = getProximoEnvio(reminder);

              return (
                <div
                  key={reminder.id}
                  style={{
                    backgroundColor: COLORS.bgCard,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '12px',
                    transition: 'all 0.2s',
                  }}
                >
                  {/* Header del recordatorio */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span style={{ fontSize: '20px' }}>
                          {reminder.tipo === 'proveedor' ? '👤' : reminder.tipo === 'objeto' ? '📄' : '📅'}
                        </span>
                        <strong style={{
                          color: COLORS.textPrimary,
                          fontSize: '15px',
                          wordBreak: 'break-word'
                        }}>
                          {getReminderTitle(reminder)}
                        </strong>
                      </div>

                      {/* Info de fecha/hora */}
                      <div style={{
                        fontSize: '13px',
                        color: COLORS.textSecondary,
                        marginLeft: '28px'
                      }}>
                        <span>{formatFecha(fecha)}</span>
                        {hora && (
                          <>
                            <span style={{ margin: '0 6px' }}>•</span>
                            <span>{formatHora12(hora)}</span>
                          </>
                        )}
                        <span style={{ margin: '0 6px' }}>•</span>
                        <span>{reminder.tipo === 'objeto' ? (reminder.modalidad === 'unico' ? 'Una vez' : reminder.modalidad || 'Una vez') : formatFrecuencia(reminder)}</span>
                        {reminder.tipo === 'objeto' && reminder.monto != null && (
                          <>
                            <span style={{ margin: '0 6px' }}>•</span>
                            <span>${parseFloat(reminder.monto).toLocaleString('es-MX')} {reminder.moneda || 'MXN'}</span>
                          </>
                        )}
                      </div>

                      {/* Proveedor o Personal */}
                      <div style={{
                        fontSize: '12px',
                        marginTop: '6px',
                        marginLeft: '28px'
                      }}>
                        {reminder.tipo === 'proveedor' ? (
                          <span style={{
                            backgroundColor: COLORS.accentLight,
                            color: COLORS.accentHover,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: 500
                          }}>
                            {reminder.proveedorNombre || 'Proveedor'}
                          </span>
                        ) : reminder.tipo === 'objeto' ? (
                          <span style={{
                            backgroundColor: '#ecfccb',
                            color: '#4d7c0f',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: 500
                          }}>
                            {reminder.documentoNombre || 'Documento'}
                          </span>
                        ) : (
                          <span style={{
                            backgroundColor: COLORS.bgSecondary,
                            color: COLORS.textSecondary,
                            padding: '2px 8px',
                            borderRadius: '12px'
                          }}>
                            Personal
                          </span>
                        )}

                        {proximo && (
                          <span style={{
                            marginLeft: '8px',
                            color: COLORS.textMuted,
                            fontStyle: 'italic'
                          }}>
                            Próximo: {proximo}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="d-flex gap-1" style={{ marginLeft: '12px' }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => startEdit(reminder)}
                        title="Editar"
                        style={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${COLORS.border}`,
                          color: COLORS.textSecondary,
                          borderRadius: '6px',
                          padding: '6px 10px',
                        }}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() => setConfirmDelete(reminder)}
                        title="Eliminar"
                        disabled={deleting === reminder.id}
                        style={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${COLORS.danger}`,
                          color: COLORS.danger,
                          borderRadius: '6px',
                          padding: '6px 10px',
                        }}
                      >
                        {deleting === reminder.id ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-trash"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Botón agregar */}
          <button
            className="btn w-100 mt-3"
            onClick={startCreate}
            style={{
              backgroundColor: COLORS.accent,
              color: '#fff',
              border: 'none',
              padding: '14px',
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Agregar Recordatorio
          </button>
        </>
      )}
    </div>
  );

  // ============= RENDER: Selector de modo =============
  const renderModeSelector = () => (
    <div className="text-center py-4">
      <h6 style={{ color: COLORS.textPrimary, marginBottom: '24px' }}>
        ¿Cómo quieres crear el recordatorio?
      </h6>

      <div className="d-flex flex-column gap-3" style={{ maxWidth: '300px', margin: '0 auto' }}>
        <button
          className="btn btn-lg"
          onClick={() => setMode('image')}
          style={{
            backgroundColor: COLORS.bgSecondary,
            border: `2px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'left',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.accent}
          onMouseOut={(e) => e.currentTarget.style.borderColor = COLORS.border}
        >
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '32px', marginRight: '16px' }}>📷</span>
            <div>
              <strong style={{ color: COLORS.textPrimary }}>Desde imagen</strong>
              <br />
              <small style={{ color: COLORS.textSecondary }}>
                Screenshots, recibos, documentos
              </small>
            </div>
          </div>
        </button>

        <button
          className="btn btn-lg"
          onClick={() => setMode('manual')}
          style={{
            backgroundColor: COLORS.bgSecondary,
            border: `2px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'left',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.accent}
          onMouseOut={(e) => e.currentTarget.style.borderColor = COLORS.border}
        >
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '32px', marginRight: '16px' }}>✏️</span>
            <div>
              <strong style={{ color: COLORS.textPrimary }}>Escribir manual</strong>
              <br />
              <small style={{ color: COLORS.textSecondary }}>
                Formulario directo
              </small>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  // ============= RENDER: Modo imagen =============
  const renderImageMode = () => (
    <div className="py-3">
      {!imagePreview ? (
        <div className="text-center">
          <label
            htmlFor="image-upload"
            style={{
              display: 'block',
              border: `2px dashed ${COLORS.border}`,
              borderRadius: '12px',
              padding: '40px 20px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.accent}
            onMouseOut={(e) => e.currentTarget.style.borderColor = COLORS.border}
          >
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📷</span>
            <p style={{ color: COLORS.textPrimary, marginBottom: '4px' }}>
              <strong>Sube o toma una foto</strong>
            </p>
            <small style={{ color: COLORS.textSecondary }}>
              Screenshots de WhatsApp, recibos, documentos
            </small>
          </label>
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          <button
            className="btn btn-link mt-3"
            onClick={() => setMode('manual')}
            style={{ color: COLORS.textSecondary }}
          >
            ← Mejor escribo manual
          </button>
        </div>
      ) : (
        <div>
          <div className="text-center mb-3">
            <img
              src={imagePreview}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
              }}
            />
          </div>

          {extractionError && (
            <div className="alert alert-warning mb-3" style={{ fontSize: '14px' }}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {extractionError}
            </div>
          )}

          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary flex-grow-1"
              onClick={() => {
                setSelectedImage(null);
                setImagePreview(null);
                setExtractedData(null);
                setExtractionError(null);
              }}
              disabled={extracting}
            >
              Cambiar imagen
            </button>

            <button
              className="btn flex-grow-1"
              onClick={handleExtractFromImage}
              disabled={extracting}
              style={{
                backgroundColor: COLORS.accent,
                color: '#fff',
                border: 'none',
              }}
            >
              {extracting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Analizando...
                </>
              ) : (
                <>
                  <i className="bi bi-magic me-2"></i>
                  Extraer datos
                </>
              )}
            </button>
          </div>

          <button
            className="btn btn-link w-100 mt-2"
            onClick={() => setMode('manual')}
            style={{ color: COLORS.textSecondary, fontSize: '14px' }}
          >
            Saltar y escribir manual →
          </button>
        </div>
      )}
    </div>
  );

  // ============= RENDER: Formulario manual =============
  const renderManualForm = () => (
    <div className="py-3">
      {/* Campo: ¿Para qué es tu recordatorio? */}
      <div className="mb-3">
        <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
          {formData.proveedorId ? 'Tipo de servicio' : '¿Para qué es tu recordatorio?'} <span style={{ color: COLORS.danger }}>*</span>
        </label>

        {/* Si hay proveedor: mostrar selector de tipo de servicio */}
        {formData.proveedorId ? (
          <>
            <select
              className="form-select"
              value={formData.tipoServicio}
              onChange={(e) => {
                handleChange('tipoServicio', e.target.value);
                // Limpiar descripción si no es "otro"
                if (e.target.value !== 'otro') {
                  handleChange('descripcionOtro', '');
                  // Usar el label como "que" para el backend
                  const tipo = TIPOS_SERVICIO_PROVEEDOR.find(t => t.value === e.target.value);
                  handleChange('que', tipo?.label || '');
                }
              }}
              style={{
                borderColor: COLORS.border,
                borderRadius: '8px',
              }}
            >
              <option value="">Selecciona un servicio...</option>
              {TIPOS_SERVICIO_PROVEEDOR.map(tipo => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.emoji} {tipo.label}
                </option>
              ))}
            </select>

            {/* Si es "otro", mostrar campo de texto */}
            {formData.tipoServicio === 'otro' && (
              <div className="mt-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Describe el servicio..."
                  value={formData.descripcionOtro}
                  onChange={(e) => {
                    handleChange('descripcionOtro', e.target.value);
                    handleChange('que', e.target.value);
                  }}
                  maxLength={100}
                  style={{
                    borderColor: COLORS.border,
                    borderRadius: '8px',
                  }}
                />
                <small style={{ color: COLORS.textMuted }}>
                  {formData.descripcionOtro.length}/100 caracteres
                </small>
              </div>
            )}
          </>
        ) : (
          /* Sin proveedor: campo de texto libre con detección de intención */
          <>
            <input
              type="text"
              className="form-control"
              placeholder="Ej: Cita con Dr. Juan, Pago de luz, Boleto de avión..."
              value={formData.que}
              onChange={(e) => handleChange('que', e.target.value)}
              onBlur={handleQueBlur}
              maxLength={100}
              style={{
                borderColor: COLORS.border,
                borderRadius: '8px',
              }}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              <small style={{ color: COLORS.textMuted }}>
                {formData.que.length}/100 caracteres
              </small>
              {tipoDetectado && TIPOS_DOCUMENTO[tipoDetectado] && (
                <small style={{
                  color: TIPOS_DOCUMENTO[tipoDetectado].color,
                  backgroundColor: `${TIPOS_DOCUMENTO[tipoDetectado].color}15`,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                }}>
                  {TIPOS_DOCUMENTO[tipoDetectado].emoji} Detectado: {TIPOS_DOCUMENTO[tipoDetectado].label}
                </small>
              )}
            </div>
          </>
        )}
      </div>

      {/* Campos: Fecha y Hora */}
      <div className="row mb-3">
        <div className="col-6">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            ¿Cuándo? <span style={{ color: COLORS.danger }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={formData.fecha}
            onChange={(e) => handleChange('fecha', e.target.value)}
            min={getMinDate()}
            style={{
              borderColor: COLORS.border,
              borderRadius: '8px',
            }}
          />
        </div>
        <div className="col-6">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            Hora
          </label>
          <TimePicker
            value={formData.hora}
            onChange={(newHora) => handleChange('hora', newHora)}
          />
        </div>
      </div>

      {/* Campo: ¿Se repite? */}
      <div className="mb-3">
        <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
          ¿Se repite?
        </label>
        <div className="d-flex flex-wrap gap-2">
          {FRECUENCIAS.map(f => (
            <button
              key={f.value}
              type="button"
              className={`btn btn-sm ${formData.repetir === f.value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleChange('repetir', f.value)}
              style={{
                borderRadius: '20px',
                padding: '6px 16px',
                fontSize: '13px',
                ...(formData.repetir === f.value ? {
                  backgroundColor: COLORS.accent,
                  borderColor: COLORS.accent,
                } : {})
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ubicación (solo si no es proveedor) */}
      {!formData.proveedorId && (
        <div className="mb-3">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            <i className="bi bi-geo-alt me-1"></i>
            Ubicación
          </label>

          {/* Tarjeta de ubicación actual (cuando existe y no está editando) */}
          {formData.ubicacion && !editandoUbicacion && (
            <div
              style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '12px',
                position: 'relative',
              }}
            >
              {/* Botón X para quitar ubicación */}
              <button
                type="button"
                onClick={() => handleChange('ubicacion', null)}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '18px',
                  lineHeight: 1,
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
                title="Quitar ubicación"
                onMouseOver={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>

              <div style={{ fontWeight: 600, color: '#0369a1', paddingRight: '24px' }}>
                {formData.ubicacion.nombre}
              </div>
              {formData.ubicacion.direccion && (
                <div style={{ fontSize: '13px', color: '#0c4a6e', marginTop: '4px' }}>
                  {formData.ubicacion.direccion}
                </div>
              )}
              {formData.ubicacion.telefono && (
                <div style={{ fontSize: '13px', color: '#0c4a6e', marginTop: '4px' }}>
                  📞 {formData.ubicacion.telefono}
                </div>
              )}
              <div className="d-flex gap-2 mt-3 flex-wrap">
                {formData.ubicacion.googleMapsUrl && (
                  <a
                    href={formData.ubicacion.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm"
                    style={{
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '12px',
                      padding: '6px 12px',
                    }}
                  >
                    <i className="bi bi-map me-1"></i>
                    Ver en mapa
                  </a>
                )}
                {formData.ubicacion.telefono && (
                  <a
                    href={`tel:${formData.ubicacion.telefono.replace(/\D/g, '')}`}
                    className="btn btn-sm"
                    style={{
                      backgroundColor: '#22c55e',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '12px',
                      padding: '6px 12px',
                    }}
                  >
                    <i className="bi bi-telephone me-1"></i>
                    Llamar
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setEditandoUbicacion(true)}
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '6px',
                    fontSize: '12px',
                    padding: '6px 12px',
                    border: '1px solid #cbd5e1',
                  }}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Cambiar
                </button>
              </div>
            </div>
          )}

          {/* Input de búsqueda de ubicación (cuando está editando o no hay ubicación) */}
          {(editandoUbicacion || !formData.ubicacion) && (
            <div
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              <input
                ref={ubicacionRefCallback}
                type="text"
                className="form-control"
                placeholder="Buscar ubicación..."
                style={{
                  borderColor: ubicacionStatus === 'ready' ? '#22c55e'
                    : ubicacionStatus === 'error' || ubicacionStatus === 'no-api' ? '#ef4444'
                    : ubicacionStatus === 'loading' ? '#3b82f6'
                    : COLORS.border,
                  borderWidth: ubicacionStatus ? '2px' : '1px',
                  borderRadius: '8px',
                  borderStyle: ubicacionStatus === 'error' ? 'dashed' : 'solid',
                }}
              />
              <small style={{ color: COLORS.textMuted, display: 'block', marginTop: '6px' }}>
                {ubicacionStatus === 'ready' && (
                  <><i className="bi bi-check-circle me-1" style={{ color: '#22c55e' }}></i>Escribe para buscar y selecciona de la lista</>
                )}
                {ubicacionStatus === 'loading' && (
                  <><i className="bi bi-hourglass-split me-1" style={{ color: '#3b82f6' }}></i>Conectando con Google Places...</>
                )}
                {ubicacionStatus === 'no-api' && (
                  <><i className="bi bi-exclamation-triangle me-1" style={{ color: '#ef4444' }}></i>Google Maps no disponible</>
                )}
                {ubicacionStatus === 'error' && (
                  <><i className="bi bi-x-circle me-1" style={{ color: '#ef4444' }}></i>Error al conectar con Places API</>
                )}
                {!ubicacionStatus && (
                  <><i className="bi bi-info-circle me-1"></i>Escribe para buscar y selecciona de la lista</>
                )}
              </small>
              {editandoUbicacion && formData.ubicacion && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={() => setEditandoUbicacion(false)}
                  style={{
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}
                >
                  Cancelar
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Warnings de la IA */}
      {formData.warnings && formData.warnings.length > 0 && (
        <div className="mb-3">
          {formData.warnings.map((w, i) => (
            <div key={i} style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '13px',
              color: '#92400e',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}>
              <i className="bi bi-exclamation-triangle" style={{ flexShrink: 0, marginTop: '1px' }}></i>
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Asunciones de la IA */}
      {formData.asunciones && formData.asunciones.length > 0 && (
        <div className="mb-3">
          {formData.asunciones.map((a, i) => (
            <div key={i} style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '13px',
              color: '#6b7280',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}>
              <i className="bi bi-info-circle" style={{ flexShrink: 0, marginTop: '1px' }}></i>
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tarjeta de detalles del documento detectado */}
      {formData.detalles && formData.tipoDocumento && formData.tipoDocumento !== 'cita_servicio' && (
        <div className="mb-4">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            Detalles detectados
          </label>
          <div
            style={{
              backgroundColor: `${TIPOS_DOCUMENTO[formData.tipoDocumento]?.color || '#84cc16'}10`,
              border: `1px solid ${TIPOS_DOCUMENTO[formData.tipoDocumento]?.color || '#84cc16'}40`,
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <div style={{
              fontWeight: 600,
              color: TIPOS_DOCUMENTO[formData.tipoDocumento]?.color || '#374151',
              marginBottom: '8px',
              fontSize: '14px'
            }}>
              {TIPOS_DOCUMENTO[formData.tipoDocumento]?.emoji} {TIPOS_DOCUMENTO[formData.tipoDocumento]?.label}
            </div>

            {/* Mostrar detalles según el tipo de documento */}
            <div style={{ fontSize: '13px', color: '#374151' }}>
              {/* Pase de abordar */}
              {formData.tipoDocumento === 'pase_abordar' && (
                <>
                  {formData.detalles.aerolinea && <div><strong>Aerolínea:</strong> {formData.detalles.aerolinea}</div>}
                  {formData.detalles.numeroVuelo && <div><strong>Vuelo:</strong> {formData.detalles.numeroVuelo}</div>}
                  {formData.detalles.origen && formData.detalles.destino && (
                    <div><strong>Ruta:</strong> {formData.detalles.origen} → {formData.detalles.destino}</div>
                  )}
                  {formData.detalles.asiento && <div><strong>Asiento:</strong> {formData.detalles.asiento}</div>}
                  {formData.detalles.terminal && <div><strong>Terminal:</strong> {formData.detalles.terminal}</div>}
                  {formData.detalles.puerta && <div><strong>Puerta:</strong> {formData.detalles.puerta}</div>}
                  {formData.detalles.confirmacion && <div><strong>Confirmación:</strong> {formData.detalles.confirmacion}</div>}
                </>
              )}

              {/* Reservación hotel */}
              {formData.tipoDocumento === 'reservacion_hotel' && (
                <>
                  {formData.detalles.hotel && <div><strong>Hotel:</strong> {formData.detalles.hotel}</div>}
                  {formData.detalles.checkIn && formData.detalles.checkOut && (
                    <div><strong>Estadía:</strong> {formData.detalles.checkIn} - {formData.detalles.checkOut}</div>
                  )}
                  {formData.detalles.habitacion && <div><strong>Habitación:</strong> {formData.detalles.habitacion}</div>}
                  {formData.detalles.noches && <div><strong>Noches:</strong> {formData.detalles.noches}</div>}
                  {formData.detalles.confirmacion && <div><strong>Confirmación:</strong> {formData.detalles.confirmacion}</div>}
                </>
              )}

              {/* Boleto transporte */}
              {formData.tipoDocumento === 'boleto_transporte' && (
                <>
                  {formData.detalles.linea && <div><strong>Línea:</strong> {formData.detalles.linea}</div>}
                  {formData.detalles.origen && formData.detalles.destino && (
                    <div><strong>Ruta:</strong> {formData.detalles.origen} → {formData.detalles.destino}</div>
                  )}
                  {formData.detalles.asiento && <div><strong>Asiento:</strong> {formData.detalles.asiento}</div>}
                  {formData.detalles.clase && <div><strong>Clase:</strong> {formData.detalles.clase}</div>}
                  {formData.detalles.confirmacion && <div><strong>Confirmación:</strong> {formData.detalles.confirmacion}</div>}
                </>
              )}

              {/* Boleto evento */}
              {formData.tipoDocumento === 'boleto_evento' && (
                <>
                  {formData.detalles.evento && <div><strong>Evento:</strong> {formData.detalles.evento}</div>}
                  {formData.detalles.venue && <div><strong>Venue:</strong> {formData.detalles.venue}</div>}
                  {formData.detalles.seccion && <div><strong>Sección:</strong> {formData.detalles.seccion}</div>}
                  {formData.detalles.fila && formData.detalles.asiento && (
                    <div><strong>Lugar:</strong> Fila {formData.detalles.fila}, Asiento {formData.detalles.asiento}</div>
                  )}
                  {formData.detalles.confirmacion && <div><strong>Confirmación:</strong> {formData.detalles.confirmacion}</div>}
                </>
              )}

              {/* Cita médica */}
              {formData.tipoDocumento === 'cita_medica' && (
                <>
                  {formData.detalles.doctor && <div><strong>Doctor:</strong> {formData.detalles.doctor}</div>}
                  {formData.detalles.especialidad && <div><strong>Especialidad:</strong> {formData.detalles.especialidad}</div>}
                  {formData.detalles.clinica && <div><strong>Clínica:</strong> {formData.detalles.clinica}</div>}
                  {formData.detalles.consultorio && <div><strong>Consultorio:</strong> {formData.detalles.consultorio}</div>}
                  {formData.detalles.confirmacion && <div><strong>Confirmación:</strong> {formData.detalles.confirmacion}</div>}
                </>
              )}

              {/* Póliza de seguro */}
              {formData.tipoDocumento === 'poliza' && (
                <>
                  {formData.detalles.tipo_seguro && <div><strong>Tipo:</strong> {formData.detalles.tipo_seguro}</div>}
                  {formData.detalles.aseguradora && <div><strong>Aseguradora:</strong> {formData.detalles.aseguradora}</div>}
                  {formData.detalles.numero_poliza && <div><strong>Póliza:</strong> {formData.detalles.numero_poliza}</div>}
                  {formData.detalles.vigencia_fin && <div><strong>Vigencia:</strong> hasta {formData.detalles.vigencia_fin}</div>}
                  {formData.detalles.pago_frecuencia && <div><strong>Pago:</strong> {formData.detalles.pago_frecuencia}</div>}
                </>
              )}

              {/* Garantía */}
              {formData.tipoDocumento === 'garantia' && (
                <>
                  {formData.detalles.producto && <div><strong>Producto:</strong> {formData.detalles.producto}</div>}
                  {formData.detalles.tienda && <div><strong>Tienda:</strong> {formData.detalles.tienda}</div>}
                  {formData.detalles.fecha_compra && <div><strong>Compra:</strong> {formData.detalles.fecha_compra}</div>}
                  {formData.detalles.duracion_meses && <div><strong>Duración:</strong> {formData.detalles.duracion_meses} meses</div>}
                </>
              )}

              {/* Contrato */}
              {formData.tipoDocumento === 'contrato' && (
                <>
                  {formData.detalles.tipo_contrato && <div><strong>Tipo:</strong> {formData.detalles.tipo_contrato}</div>}
                  {formData.detalles.contraparte && <div><strong>Contraparte:</strong> {formData.detalles.contraparte}</div>}
                  {formData.detalles.vigencia_fin && <div><strong>Vigencia:</strong> hasta {formData.detalles.vigencia_fin}</div>}
                  {formData.detalles.dia_pago && <div><strong>Día de pago:</strong> {formData.detalles.dia_pago} de cada mes</div>}
                  {formData.detalles.monto_periodico && <div><strong>Monto:</strong> ${formData.detalles.monto_periodico}</div>}
                </>
              )}

              {/* Pago de servicio */}
              {formData.tipoDocumento === 'pago_servicio' && (
                <>
                  {formData.detalles.tipo_servicio && (
                    <div>
                      <strong>Servicio:</strong>{' '}
                      {ICONOS_PAGO_SERVICIO[formData.detalles.tipo_servicio] || ''}{' '}
                      {formData.detalles.tipo_servicio.charAt(0).toUpperCase() + formData.detalles.tipo_servicio.slice(1)}
                    </div>
                  )}
                  {formData.detalles.empresa && <div><strong>Empresa:</strong> {formData.detalles.empresa}</div>}
                  {formData.detalles.monto && (
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#dc2626', marginTop: '4px' }}>
                      ${formData.detalles.monto}
                    </div>
                  )}
                  {formData.detalles.numero_cuenta && <div><strong>Cuenta:</strong> {formData.detalles.numero_cuenta}</div>}
                  {formData.detalles.periodo && <div><strong>Periodo:</strong> {formData.detalles.periodo}</div>}
                </>
              )}
            </div>

            {/* Anticipación sugerida */}
            {formData.anticipacionSugerida && formData.anticipacionSugerida !== 120 && (
              <div style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#6b7280',
                borderTop: '1px solid #e5e7eb',
                paddingTop: '8px'
              }}>
                ⏰ Te avisaremos {formData.anticipacionSugerida >= 1440
                  ? `${Math.floor(formData.anticipacionSugerida / 1440)} día${formData.anticipacionSugerida >= 2880 ? 's' : ''} antes`
                  : formData.anticipacionSugerida >= 60
                    ? `${Math.floor(formData.anticipacionSugerida / 60)} hora${formData.anticipacionSugerida >= 120 ? 's' : ''} antes`
                    : `${formData.anticipacionSugerida} minutos antes`}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Acciones sugeridas */}
      {formData.acciones_sugeridas && (
        <div className="mb-4">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            Acciones sugeridas
          </label>
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            padding: '12px',
          }}>
            {/* Crear recordatorio - siempre marcado */}
            <label className="d-flex align-items-center gap-2 mb-2" style={{ cursor: 'default' }}>
              <input type="checkbox" checked disabled style={{ accentColor: COLORS.accent }} />
              <span style={{ fontSize: '14px', color: COLORS.textPrimary }}>
                <i className="bi bi-bell me-1"></i>
                Crear recordatorio
              </span>
            </label>

            {/* Guardar en bóveda - visible para poliza, garantia, contrato; opcional para pago_servicio */}
            {(formData.acciones_sugeridas?.guardar_boveda || ['poliza', 'garantia', 'contrato'].includes(formData.tipoDocumento)) && (
              <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.acciones_sugeridas?.guardar_boveda || false}
                  onChange={(e) => handleChange('acciones_sugeridas', {
                    ...(formData.acciones_sugeridas || {}),
                    guardar_boveda: e.target.checked,
                  })}
                  style={{ accentColor: COLORS.accent }}
                />
                <span style={{ fontSize: '14px', color: COLORS.textPrimary }}>
                  <i className="bi bi-safe me-1"></i>
                  Guardar documento en bóveda
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Guardar en favoritos - sección expandida para cita_medica/cita_servicio */}
      {['cita_medica', 'cita_servicio'].includes(formData.tipoDocumento) && (
        <div className="mb-4">
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '4px', fontSize: '14px' }}>
              <i className="bi bi-geo-alt-fill me-1"></i>
              Guardar en favoritos
            </div>
            <div style={{ fontSize: '13px', color: '#a16207', marginBottom: '12px' }}>
              ¿Quieres guardar este lugar para futuras citas?
            </div>

            <div className="mb-2">
              <label style={{ fontSize: '13px', color: COLORS.textPrimary, fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                Nombre del lugar o doctor
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Ej: Dr. García, Clínica Santa Rita..."
                value={formData.favoritoNombre}
                onChange={(e) => handleChange('favoritoNombre', e.target.value)}
                maxLength={100}
                style={{ borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <div className="mb-2">
              <label style={{ fontSize: '13px', color: COLORS.textPrimary, fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                Teléfono <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>(opcional)</span>
              </label>
              <input
                type="tel"
                className="form-control form-control-sm"
                placeholder="Ej: 55 1234 5678"
                value={formData.favoritoTelefono}
                onChange={(e) => handleChange('favoritoTelefono', e.target.value)}
                maxLength={20}
                style={{ borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <label className="d-flex align-items-center gap-2 mt-3" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.guardarFavorito}
                onChange={(e) => handleChange('guardarFavorito', e.target.checked)}
                style={{ accentColor: '#f59e0b' }}
              />
              <span style={{ fontSize: '14px', color: '#92400e', fontWeight: 500 }}>
                <i className="bi bi-star-fill me-1"></i>
                Guardar en mis favoritos
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Campo: Proveedor (solo en creación, solo para mascotas) */}
      {!editingReminder && objetoTipo === 'mascota' && (
        <div className="mb-4">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            Proveedor <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>(opcional)</span>
          </label>
          <select
            className="form-select"
            value={formData.proveedorId}
            onChange={(e) => handleChange('proveedorId', e.target.value)}
            disabled={loadingProveedores}
            style={{
              borderColor: COLORS.border,
              borderRadius: '8px',
            }}
          >
            <option value="">Ninguno (recordatorio personal)</option>
            {proveedores.map(p => (
              <option key={p.id} value={p.id}>
                {p.nombre} {p.tipoServicio ? `(${p.tipoServicio})` : ''}
              </option>
            ))}
          </select>
          {formData.proveedorId && (
            <small style={{ color: COLORS.accent }}>
              <i className="bi bi-info-circle me-1"></i>
              El proveedor recibirá una notificación automática
            </small>
          )}
        </div>
      )}

      {/* Info sobre edición de proveedor */}
      {editingReminder?.tipo === 'proveedor' && (
        <div
          className="mb-3 p-3"
          style={{
            backgroundColor: COLORS.accentLight,
            borderRadius: '8px',
            border: `1px solid ${COLORS.accent}`,
          }}
        >
          <small style={{ color: COLORS.accentHover }}>
            <i className="bi bi-person-check me-2"></i>
            <strong>Proveedor:</strong> {editingReminder.proveedorNombre}
            <br />
            <span style={{ color: COLORS.textSecondary }}>
              Al guardar, el proveedor recibirá una notificación de los cambios.
            </span>
          </small>
        </div>
      )}

      {/* Info de avisos */}
      <div
        className="mb-3 p-3"
        style={{
          backgroundColor: COLORS.accentLight,
          borderRadius: '8px',
          border: `1px solid #bbf7d0`,
        }}
      >
        <small style={{ color: '#166534' }}>
          <i className="bi bi-bell me-2"></i>
          <strong>Recibirás avisos:</strong>
          <br />
          {formData.proveedorId || editingReminder?.tipo === 'proveedor' ? (
            <>• 2 horas antes del evento (al proveedor)</>
          ) : (
            <>
              • 1 día antes a las 9:00 AM
              <br />
              • 2 horas antes del evento
            </>
          )}
        </small>
      </div>

      {/* Contador */}
      {!editingReminder && (
        <div className="text-center mb-3">
          <small style={{ color: COLORS.textMuted }}>
            {totalActivos} recordatorios activos
          </small>
        </div>
      )}
    </div>
  );

  // ============= RENDER: Modal de confirmación de eliminación =============
  const renderDeleteConfirm = () => {
    if (!confirmDelete) return null;

    return (
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 100000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
        onClick={() => setConfirmDelete(null)}
      >
        <div
          className="card"
          style={{
            maxWidth: '400px',
            width: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="card-body p-4 text-center">
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            <h5 style={{ color: COLORS.textPrimary, marginBottom: '8px' }}>
              ¿Eliminar recordatorio?
            </h5>
            <p style={{ color: COLORS.textSecondary, marginBottom: '24px' }}>
              "{getReminderTitle(confirmDelete)}"
              {confirmDelete.tipo === 'proveedor' && (
                <>
                  <br />
                  <small style={{ color: COLORS.accent }}>
                    El proveedor será notificado de la cancelación
                  </small>
                </>
              )}
            </p>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary flex-grow-1"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="btn flex-grow-1"
                onClick={() => handleDelete(confirmDelete)}
                disabled={deleting}
                style={{
                  backgroundColor: COLORS.danger,
                  color: '#fff',
                  border: 'none',
                }}
              >
                {deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-2"></i>
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============= RENDER: Lista de rutinas para MASCOTAS =============
  const renderPetList = () => {
    // Obtener rutinas disponibles para esta especie
    const rutinasDisponibles = Object.entries(RUTINAS_CONFIG)
      .filter(([_, config]) => config.especies.includes(especieNormalizada))
      .map(([key, config]) => ({ key, ...config }));

    // Verificar qué rutinas ya existen (para deshabilitar)
    const rutinasExistentes = rutinas.map(r => r.tipoActividad);

    // Límite de rutinas según plan
    const limiteRutinas = LIMITE_RUTINAS[objetoPlan] || 1;
    const puedeCrearMas = rutinas.length < limiteRutinas;

    return (
      <div className="py-2">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: COLORS.accent }} role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-2" style={{ color: COLORS.textSecondary }}>
              Cargando recordatorios...
            </p>
          </div>
        ) : (
          <>
            {/* ======= SECCIÓN 1: RUTINAS ======= */}
            <div style={{ marginBottom: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
              <div
                onClick={() => setSeccionesAbiertas(p => ({ ...p, rutinas: !p.rutinas }))}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', backgroundColor: COLORS.bgSecondary, cursor: 'pointer', userSelect: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className={`bi bi-chevron-${seccionesAbiertas.rutinas ? 'down' : 'right'}`} style={{ fontSize: '12px', color: COLORS.textMuted }}></i>
                  <span style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '14px' }}>
                    Rutinas ({rutinas.length}/{limiteRutinas})
                  </span>
                </div>
                {puedeCrearMas && (
                  <div onClick={(e) => e.stopPropagation()}>
                    {isMobile ? (
                      <select
                        className="form-select form-select-sm"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            setSelectedRutina(e.target.value);
                            setRutinaForm({ hora: '08:00', diasSemana: ['L', 'M', 'X', 'J', 'V'] });
                            setView('rutina');
                          }
                        }}
                        style={{ fontSize: '11px', padding: '4px 8px', borderColor: COLORS.accent, borderRadius: '6px', maxWidth: '140px' }}
                      >
                        <option value="">+ Agregar</option>
                        {rutinasDisponibles.map(({ key, emoji, label }) => {
                          const yaExiste = rutinasExistentes.includes(key);
                          return <option key={key} value={key} disabled={yaExiste}>{emoji} {label}{yaExiste ? ' (ya existe)' : ''}</option>;
                        })}
                      </select>
                    ) : (
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          // Open the first available rutina
                          const available = rutinasDisponibles.find(r => !rutinasExistentes.includes(r.key));
                          if (available) {
                            setSelectedRutina(available.key);
                            setRutinaForm({ hora: '08:00', diasSemana: ['L', 'M', 'X', 'J', 'V'] });
                            setView('rutina');
                          }
                        }}
                        style={{ fontSize: '11px', padding: '4px 10px', backgroundColor: '#fff', color: COLORS.accent, border: `1.5px solid ${COLORS.accent}`, borderRadius: '6px', fontWeight: 500 }}
                      >
                        <i className="bi bi-plus me-1"></i>Agregar
                      </button>
                    )}
                  </div>
                )}
              </div>
              {seccionesAbiertas.rutinas && (
                <div style={{ padding: '10px 16px' }}>
                  {rutinas.length > 0 ? rutinas.map((rutina) => {
                    const config = RUTINAS_CONFIG[rutina.tipoActividad] || { emoji: '📅', label: rutina.tipoActividad };
                    return (
                      <div
                        key={rutina.id}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 12px', marginBottom: '8px', borderRadius: '10px',
                          backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <span style={{ fontSize: '22px' }}>{config.emoji}</span>
                          <div>
                            <div style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '14px' }}>{config.label}</div>
                            <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                              {formatHora12(rutina.hora)} • {rutina.diasSemana?.join('-') || 'Todos'}
                            </div>
                          </div>
                        </div>
                        <button className="btn btn-sm" onClick={() => handleDeleteRutina(rutina)} disabled={deleting === rutina.id}
                          style={{ backgroundColor: 'transparent', border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: '6px', padding: '5px 9px' }}>
                          {deleting === rutina.id ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
                        </button>
                      </div>
                    );
                  }) : (
                    <div style={{ fontSize: '13px', color: COLORS.textMuted, padding: '8px 0' }}>Sin rutinas configuradas</div>
                  )}
                </div>
              )}
            </div>

            {/* ======= SECCIÓN 2: TRATAMIENTOS (solo Premium) ======= */}
            {console.log('[QuickReminder] objetoPlan:', JSON.stringify(objetoPlan), '| tratamientosActivos:', tratamientosActivos.length)}
            {objetoPlan === 'premium' ? (
              <div style={{ marginBottom: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                <div
                  onClick={() => setSeccionesAbiertas(p => ({ ...p, tratamientos: !p.tratamientos }))}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', backgroundColor: COLORS.bgSecondary, cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className={`bi bi-chevron-${seccionesAbiertas.tratamientos ? 'down' : 'right'}`} style={{ fontSize: '12px', color: COLORS.textMuted }}></i>
                    <span style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '14px' }}>
                      {tratamientosActivos.length > 0
                        ? `Tratamiento: ${tratamientosActivos[0].medicamentos?.[0]?.nombre || 'Activo'}`
                        : 'Tratamientos'}
                    </span>
                    {tratamientosActivos.length > 0 && (
                      <span style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                        Activo
                      </span>
                    )}
                  </div>
                </div>
                {seccionesAbiertas.tratamientos && (
                  <div style={{ padding: '10px 16px' }}>
                    {tratamientosActivos.length > 0 ? tratamientosActivos.map((trat) => {
                      const progreso = trat.diasTotales > 0 ? Math.round((trat.diaActual / trat.diasTotales) * 100) : 0;
                      return (
                        <div key={trat.id} style={{ marginBottom: '10px' }}>
                          {/* Progreso */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', color: COLORS.textSecondary }}>
                            <span>📊 Día {trat.diaActual}/{trat.diasTotales}</span>
                            <span>•</span>
                            <span>🔥 {trat.racha || 0} día{(trat.racha || 0) !== 1 ? 's' : ''}</span>
                          </div>
                          {/* Barra de progreso */}
                          <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '10px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(progreso, 100)}%`, backgroundColor: COLORS.accent, borderRadius: '4px', transition: 'width 0.3s' }}></div>
                          </div>
                          {/* Medicamentos */}
                          {(trat.medicamentos || []).map((med, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: COLORS.textPrimary, marginBottom: '4px' }}>
                              <span>💊</span>
                              <span style={{ fontWeight: 500 }}>{med.nombre} {med.dosis}</span>
                              <span style={{ color: COLORS.textMuted }}>— cada {med.frecuenciaHoras}h</span>
                            </div>
                          ))}
                          {/* Botón Olvidé algo */}
                          <button
                            className="btn btn-sm mt-2"
                            onClick={async () => {
                              const hoy = new Date().toISOString().split('T')[0];
                              const resp = await reportarOlvido(trat.id, hoy, ['general']);
                              if (!resp.error) {
                                toast.success('Olvido registrado');
                                loadRecordatorios();
                              } else {
                                toast.error(resp.error);
                              }
                            }}
                            style={{ fontSize: '12px', padding: '5px 12px', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '8px' }}
                          >
                            😅 Olvidé algo hoy
                          </button>
                        </div>
                      );
                    }) : (
                      <div style={{ fontSize: '13px', color: COLORS.textMuted, padding: '8px 0' }}>Sin tratamientos activos</div>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {/* ======= SECCIÓN 3: PRÓXIMAS (solo Premium) ======= */}
            {objetoPlan === 'premium' ? (
              <div style={{ marginBottom: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                <div
                  onClick={() => setSeccionesAbiertas(p => ({ ...p, proximas: !p.proximas }))}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', backgroundColor: COLORS.bgSecondary, cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className={`bi bi-chevron-${seccionesAbiertas.proximas ? 'down' : 'right'}`} style={{ fontSize: '12px', color: COLORS.textMuted }}></i>
                    <span style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '14px' }}>
                      Próximas{proximas.length > 0 ? ` (${proximas.length})` : ''}
                    </span>
                  </div>
                </div>
                {seccionesAbiertas.proximas && (
                  <div style={{ padding: '10px 16px' }}>
                    {proximas.length > 0 ? proximas.map((rec) => {
                      const esVencida = rec.fecha && new Date(rec.fecha + 'T23:59:59') < new Date();
                      const emoji = rec.que?.startsWith('Vacuna') ? '💉' : rec.que?.startsWith('Desparasitación') ? '💊' : '📅';
                      return (
                        <div
                          key={rec.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 10px', marginBottom: '6px', borderRadius: '8px',
                            backgroundColor: esVencida ? '#fef2f2' : '#f0fdf4',
                            border: `1px solid ${esVencida ? '#fecaca' : '#bbf7d0'}`,
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>{esVencida ? '⚠️' : emoji}</span>
                          <div style={{ flex: 1, fontSize: '13px' }}>
                            <span style={{ fontWeight: 500, color: COLORS.textPrimary }}>{rec.que}</span>
                            <span style={{ color: esVencida ? '#dc2626' : COLORS.textMuted, marginLeft: '6px' }}>
                              — {formatFecha(rec.fecha)}
                            </span>
                          </div>
                          {rec.enviado1Dia && !rec.enviado2Horas && (
                            <span style={{ fontSize: '10px', color: '#059669', backgroundColor: '#d1fae5', padding: '2px 6px', borderRadius: '4px' }}>1 aviso enviado</span>
                          )}
                          {rec.enviado1Dia && rec.enviado2Horas && (
                            <span style={{ fontSize: '10px', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>Enviados</span>
                          )}
                        </div>
                      );
                    }) : (
                      <div style={{ fontSize: '13px', color: COLORS.textMuted, padding: '8px 0' }}>Sin vacunas o desparasitaciones próximas</div>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {/* ======= SECCIÓN 4: PROVEEDORES ======= */}
            {(objetoPlan === 'standard' || objetoPlan === 'premium') ? (
              <div style={{ marginBottom: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                <div
                  onClick={() => setSeccionesAbiertas(p => ({ ...p, proveedores: !p.proveedores }))}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', backgroundColor: COLORS.bgSecondary, cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className={`bi bi-chevron-${seccionesAbiertas.proveedores ? 'down' : 'right'}`} style={{ fontSize: '12px', color: COLORS.textMuted }}></i>
                    <span style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '14px' }}>
                      Proveedores{recordatorios.length > 0 ? ` (${recordatorios.length})` : ''}
                    </span>
                  </div>
                  {proveedores.length > 0 && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-sm"
                        onClick={() => { setMode('manual'); setView('create'); }}
                        style={{ fontSize: '11px', padding: '4px 10px', backgroundColor: '#fff', color: COLORS.accent, border: `1.5px solid ${COLORS.accent}`, borderRadius: '6px', fontWeight: 500 }}
                      >
                        <i className="bi bi-plus me-1"></i>Agregar
                      </button>
                    </div>
                  )}
                </div>
                {seccionesAbiertas.proveedores && (
                  <div style={{ padding: '10px 16px' }}>
                    {recordatorios.length > 0 ? recordatorios.map((rec) => (
                      <div
                        key={rec.id}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 12px', marginBottom: '8px', borderRadius: '10px',
                          backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span style={{ fontSize: '16px' }}>👤</span>
                            <strong style={{ color: COLORS.textPrimary, fontSize: '14px' }}>{rec.proveedorNombre}</strong>
                          </div>
                          <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginLeft: '24px' }}>
                            {traducirTipoServicio(rec.tipoServicio)} • {formatHora12(rec.horaServicio)}
                          </div>
                        </div>
                        <button className="btn btn-sm" onClick={() => setConfirmDelete(rec)} disabled={deleting === rec.id}
                          style={{ backgroundColor: 'transparent', border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: '6px', padding: '5px 9px' }}>
                          {deleting === rec.id ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
                        </button>
                      </div>
                    )) : (
                      <div style={{ fontSize: '13px', color: COLORS.textMuted, padding: '8px 0' }}>Sin proveedores configurados</div>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {/* Plan lock para secciones no disponibles */}
            {objetoPlan === 'light' && (
              <div style={{ marginBottom: '12px', padding: '14px 16px', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', textAlign: 'center' }}>
                <span style={{ fontSize: '18px' }}>🔒</span>
                <p style={{ color: '#92400e', fontSize: '13px', margin: '6px 0 0' }}>
                  Tratamientos, próximas y proveedores disponibles en plan Standard o Premium
                </p>
              </div>
            )}
            {objetoPlan === 'standard' && (
              <div style={{ marginBottom: '12px', padding: '14px 16px', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', textAlign: 'center' }}>
                <span style={{ fontSize: '18px' }}>🔒</span>
                <p style={{ color: '#92400e', fontSize: '13px', margin: '6px 0 0' }}>
                  Tratamientos y próximas disponibles en plan Premium
                </p>
              </div>
            )}

            {/* Estado vacío global */}
            {rutinas.length === 0 && recordatorios.length === 0 && proximas.length === 0 && tratamientosActivos.length === 0 && (
              <div className="text-center py-3">
                <div style={{ fontSize: '40px', marginBottom: '12px', opacity: 0.5 }}>🔔</div>
                <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                  No tienes recordatorios activos
                </p>
                <p style={{ color: COLORS.textMuted, fontSize: '13px' }}>
                  Agrega una rutina diaria para tu mascota
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ============= RENDER: Formulario de rutina =============
  const renderRutinaForm = () => {
    if (!selectedRutina) return null;

    const config = RUTINAS_CONFIG[selectedRutina];
    const esPaseo = selectedRutina === 'paseo';

    return (
      <div className="py-3">
        <div className="text-center mb-4">
          <span style={{ fontSize: '48px' }}>{config.emoji}</span>
          <h5 style={{ color: COLORS.textPrimary, marginTop: '8px' }}>
            {config.label} diario
          </h5>
        </div>

        {/* Selector de hora */}
        <div className="mb-4">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            ¿A qué hora?
          </label>
          <TimePicker
            value={rutinaForm.hora}
            onChange={(newHora) => setRutinaForm(prev => ({ ...prev, hora: newHora }))}
          />
        </div>

        {/* Selector de días */}
        <div className="mb-4">
          <label className="form-label" style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            Días:
          </label>
          <div className="d-flex justify-content-between gap-1">
            {DIAS_SEMANA.map(dia => {
              const isSelected = rutinaForm.diasSemana.includes(dia.value);
              return (
                <button
                  key={dia.value}
                  type="button"
                  className="btn"
                  onClick={() => {
                    setRutinaForm(prev => ({
                      ...prev,
                      diasSemana: isSelected
                        ? prev.diasSemana.filter(d => d !== dia.value)
                        : [...prev.diasSemana, dia.value]
                    }));
                  }}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    padding: 0,
                    backgroundColor: isSelected ? COLORS.accent : COLORS.bgSecondary,
                    color: isSelected ? '#fff' : COLORS.textSecondary,
                    border: `2px solid ${isSelected ? COLORS.accent : COLORS.border}`,
                    fontWeight: 600,
                    fontSize: '13px',
                  }}
                  title={dia.nombre}
                >
                  {dia.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Info de clima (solo paseo) */}
        {esPaseo && (
          <div
            className="mb-4 p-3"
            style={{
              backgroundColor: '#fff7ed',
              borderRadius: '8px',
              border: '1px solid #fed7aa',
            }}
          >
            <small style={{ color: '#c2410c' }}>
              <i className="bi bi-sun me-2"></i>
              <strong>Incluiremos el clima en tu aviso</strong>
            </small>
          </div>
        )}

        {/* Info de recordatorio */}
        <div
          className="mb-4 p-3"
          style={{
            backgroundColor: COLORS.accentLight,
            borderRadius: '8px',
            border: '1px solid #bbf7d0',
          }}
        >
          <small style={{ color: '#166534' }}>
            <i className="bi bi-bell me-2"></i>
            <strong>Recibirás aviso 15 minutos antes</strong>
          </small>
        </div>

        {/* Botones */}
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary flex-grow-1"
            onClick={() => {
              setSelectedRutina(null);
              setView('list');
            }}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="btn flex-grow-1"
            onClick={handleSaveRutina}
            disabled={saving || rutinaForm.diasSemana.length === 0}
            style={{
              backgroundColor: COLORS.accent,
              color: '#fff',
              border: 'none',
            }}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Creando...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Crear
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Handler para guardar rutina
  const handleSaveRutina = async () => {
    if (!selectedRutina || rutinaForm.diasSemana.length === 0) {
      toast.error('Selecciona al menos un día');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tipoActividad: selectedRutina,
          hora: rutinaForm.hora,
          diasSemana: rutinaForm.diasSemana,
          activo: true,
          incluirClima: selectedRutina === 'paseo', // Siempre incluir clima para paseos
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al crear rutina');
      }

      toast.success('Rutina creada correctamente');
      await loadRecordatorios();
      setSelectedRutina(null);
      setView('list');
      onSuccess?.();

    } catch (error) {
      console.error('Error creando rutina:', error);
      toast.error(error.message || 'Error al crear la rutina');
    } finally {
      setSaving(false);
    }
  };

  // Handler para eliminar rutina
  const handleDeleteRutina = async (rutina) => {
    setDeleting(rutina.id);
    try {
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio/${rutina.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al eliminar');
      }

      toast.success('Rutina eliminada');
      await loadRecordatorios();

    } catch (error) {
      console.error('Error eliminando rutina:', error);
      toast.error(error.message || 'Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  // Helper para traducir tipo de servicio
  const traducirTipoServicio = (tipo) => {
    const traducciones = {
      paseo: 'Paseo',
      alimentacion: 'Alimentación',
      limpieza: 'Limpieza',
      medicamento: 'Medicamento',
      bano: 'Baño',
      veterinario: 'Veterinario',
      otro: 'Servicio',
    };
    return traducciones[tipo] || 'Servicio';
  };

  // ============= RENDER PRINCIPAL =============
  // Usar Portal para renderizar fuera del stacking context del panel
  return createPortal(
    <>
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: 99999,
          display: 'flex',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'center',
          padding: isMobile ? '0' : '16px',
        }}
        onClick={onClose}
      >
        <div
          className="card shadow"
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '520px',
            maxHeight: isMobile ? '100%' : '90vh',
            height: isMobile ? '100%' : 'auto',
            overflow: 'hidden',
            borderRadius: isMobile ? '0' : '16px',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: COLORS.accent,
              borderBottom: 'none',
              padding: '16px 20px',
            }}
          >
            <div className="d-flex align-items-center">
              {(view === 'create' || view === 'edit' || view === 'rutina') && (
                <button
                  className="btn btn-sm me-2"
                  onClick={() => {
                    if (view === 'rutina') {
                      setSelectedRutina(null);
                      setView('list');
                    } else if (mode === null || mode === 'image') {
                      setView('list');
                      resetForm();
                    } else if (mode === 'manual' && extractedData) {
                      setMode('image');
                    } else {
                      if (esMascota) {
                        setView('list');
                        resetForm();
                      } else {
                        setMode(null);
                      }
                    }
                  }}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: '#fff',
                    borderRadius: '6px',
                    padding: '4px 10px',
                  }}
                >
                  <i className="bi bi-arrow-left"></i>
                </button>
              )}
              <h5 className="mb-0" style={{ color: '#fff', fontWeight: 600 }}>
                <i className="bi bi-bell me-2"></i>
                {view === 'list' && 'Recordatorios'}
                {view === 'create' && 'Nuevo Recordatorio'}
                {view === 'edit' && 'Editar Recordatorio'}
                {view === 'rutina' && 'Nueva Rutina'}
              </h5>
            </div>

            <div className="d-flex align-items-center">
              {view === 'list' && (
                <span
                  className="badge me-2"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontSize: '13px',
                    padding: '6px 12px',
                  }}
                >
                  {esMascota
                    ? `${rutinas.length + recordatorios.length + proximas.length + tratamientosActivos.length} activos`
                    : `${totalActivos} activos`}
                </span>
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              />
            </div>
          </div>

          {/* Subtítulo con nombre del objeto + toggle Google Calendar */}
          <div
            className="px-4 py-2 d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: COLORS.bgSecondary,
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <small style={{ color: COLORS.textSecondary }}>
              {objetoTipo === 'mascota' ? '🐾' : '🔑'}{' '}
              <strong style={{ color: COLORS.textPrimary }}>{objetoNombre}</strong>
            </small>

            {/* Google Calendar toggle + refresh (solo objetos, solo vista list, solo si conectado) */}
            {!esMascota && view === 'list' && calendarConnected && (
              <div className="d-flex align-items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); loadGoogleCalendarEvents(true); }}
                  title="Recargar eventos de Google Calendar"
                  disabled={googleEventsLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: googleEventsLoading ? COLORS.textMuted : '#4285F4',
                    cursor: googleEventsLoading ? 'default' : 'pointer',
                    padding: '2px 4px',
                    fontSize: '13px',
                  }}
                >
                  <i className={`bi bi-arrow-clockwise${googleEventsLoading ? ' spin-animation' : ''}`}></i>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleGoogleEvents(); }}
                  title={showGoogleEvents ? 'Ocultar eventos de Google Calendar' : 'Mostrar eventos de Google Calendar'}
                  style={{
                    background: showGoogleEvents ? '#E3F2FD' : 'transparent',
                    border: `1px solid ${showGoogleEvents ? '#4285F4' : COLORS.borderLight}`,
                    color: showGoogleEvents ? '#4285F4' : COLORS.textMuted,
                    cursor: 'pointer',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  📆 {showGoogleEvents ? 'on' : 'off'}
                </button>
              </div>
            )}
          </div>

          {/* Body */}
          <div
            className="card-body"
            style={{
              overflowY: 'auto',
              padding: '16px 20px',
              flex: 1,
            }}
          >
            {/* Vista de lista - diferente para mascotas vs objetos */}
            {view === 'list' && (esMascota ? renderPetList() : renderObjectKanban())}

            {/* Vista de rutina - solo para mascotas */}
            {view === 'rutina' && esMascota && renderRutinaForm()}

            {/* Vista de crear/editar - objetos: imagen o manual, mascotas: solo manual (proveedores) */}
            {(view === 'create' || view === 'edit') && (
              <>
                {!esMascota && mode === null && renderModeSelector()}
                {!esMascota && mode === 'image' && renderImageMode()}
                {mode === 'manual' && renderManualForm()}
              </>
            )}
          </div>

          {/* Footer - Solo mostrar botones en modo manual */}
          {(view === 'create' || view === 'edit') && mode === 'manual' && (
            <div
              className="card-footer d-flex gap-2"
              style={{
                backgroundColor: COLORS.bgSecondary,
                borderTop: `1px solid ${COLORS.border}`,
                padding: '16px 20px',
              }}
            >
              <button
                className="btn btn-outline-secondary flex-grow-1"
                onClick={() => {
                  setView('list');
                  resetForm();
                }}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                className="btn flex-grow-1"
                onClick={handleSave}
                disabled={
                  saving ||
                  !formData.fecha ||
                  // Validación diferente según si hay proveedor
                  (formData.proveedorId
                    ? (!formData.tipoServicio || (formData.tipoServicio === 'otro' && !formData.descripcionOtro.trim()))
                    : !formData.que.trim())
                }
                style={{
                  backgroundColor: COLORS.accent,
                  color: '#fff',
                  border: 'none',
                }}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-2"></i>
                    {editingReminder ? 'Guardar Cambios' : 'Crear Recordatorio'}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      {renderDeleteConfirm()}
    </>,
    document.body
  );
}
